import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Button, TextField, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Paper, Chip, Collapse, Card, CardContent } from '@mui/material';
import { Comment, Download, Send, Delete, Save, Edit, Close, ExpandMore, ExpandLess, Assessment } from '@mui/icons-material';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import { mockSurveyService } from '@services/mocksurvey';
import { toast } from 'react-toastify';
import {
  FEATURE_NAMES,
  canUpdateByName,
} from '@utils/permissions';

// Extend Window interface for tracking logged comments
declare global {
  interface Window {
    __loggedInvalidComments?: Set<string>;
  }
}

const ViewerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const Toolbar = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
`;

const DocumentArea = styled(Box)`
  flex: 1;
  overflow: auto;
  position: relative;
  background: #fafafa;
  padding: 24px;
`;

const DocxContainer = styled(Box)`
  max-width: 100%;
  margin: 0 auto;
  background: #f5f5f5;
  padding: 24px;
  position: relative;
  
  /* Preserve ALL original document styles */
  .docx-wrapper {
    max-width: 100%;
  }
  
  /* Preserve all docx-preview styles - don't override */
  .docx {
    font-family: inherit !important;
    line-height: inherit !important;
    color: inherit !important;
  }
  
  /* Ensure tables have borders and proper styling */
  .docx table,
  .docx-wrapper table {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    width: 100% !important;
    margin: 0 !important;
  }
  
  .docx table td,
  .docx table th,
  .docx-wrapper table td,
  .docx-wrapper table th {
    border: 1px solid #000 !important;
    padding: 4px 8px !important;
    vertical-align: top !important;
  }
  
  .docx table th,
  .docx-wrapper table th {
    background-color: #f0f0f0 !important;
    font-weight: bold !important;
  }
  
  /* Preserve all fonts */
  .docx *,
  .docx-wrapper * {
    font-family: inherit !important;
  }
  
  /* Page-like styling for pagination */
  .docx-wrapper > section,
  .docx > section,
  section.docx-section {
    background: white !important;
    width: 8.5in !important;
    min-height: 11in !important;
    margin: 0 auto 24px auto !important;
    padding: 1in !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    page-break-after: always !important;
    page-break-inside: avoid !important;
    position: relative !important;
    overflow: visible !important;
    
    @media print {
      box-shadow: none !important;
      margin: 0 !important;
      page-break-after: always !important;
    }
    
    @media (max-width: 1200px) {
      width: 100% !important;
      max-width: 8.5in !important;
      padding: 0.75in !important;
    }
    
    @media (max-width: 768px) {
      padding: 0.5in !important;
    }
  }
  
  /* Ensure paragraphs respect page breaks */
  .docx p,
  .docx div,
  .docx-wrapper p,
  .docx-wrapper div {
    page-break-inside: avoid;
    orphans: 3;
    widows: 3;
  }
  
  /* Page break indicators */
  .docx-wrapper > section:not(:last-child)::after,
  .docx > section:not(:last-child)::after,
  section.docx-section:not(:last-child)::after {
    content: '';
    display: block;
    height: 1px;
    background: transparent;
  }
  
  /* If docx-preview renders without sections, wrap content in pages */
  .docx-wrapper:not(:has(> section)) {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
`;

const CommentOverviewPanel = styled(Paper)`
  margin-bottom: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-left: 4px solid #5BBFD6;
`;

const CommentSummaryCard = styled(Card)`
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid #ff9800;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-left-color: #f57c00;
  }
`;



const CommentItem = styled(Box)`
  padding: 12px;
  margin-bottom: 12px;
  background: #f9f9f9;
  border-left: 3px solid #2196f3;
  border-radius: 4px;
`;

const CommentText = styled(Typography)`
  font-size: 14px;
  margin-bottom: 8px;
`;

const CommentAuthor = styled(Typography)`
  font-size: 12px;
  color: #666;
  font-style: italic;
`;

export interface CommentData {
  id: string;
  x: number;
  y: number;
  text: string;
  author: string;
  timestamp: Date;
  selectedText?: string;
  range?: Range | null;
  paragraphIndex?: number; // Index of the paragraph containing the comment
  occurrence?: number; // Nth occurrence of the selected text within that paragraph (1-based)
  contextText?: string; // Store surrounding text for better matching
  beforeText?: string; // Text before the selection
  afterText?: string; // Text after the selection
  startOffset?: number; // Character offset within paragraph (from API)
  endOffset?: number; // Character offset within paragraph (from API)
  parentId?: string; // ID of parent comment if this is a reply
  replies?: CommentData[]; // Nested replies
  docxCommentId?: string; // Original DOCX comment ID for matching replies
  fileName?: string; // File name from API response
  initials?: string; // User initials from API response
}

interface DocumentViewerProps {
  docxBlob: Blob;
  onDownload?: (blob: Blob) => void; // Frontend download (embeds comments in frontend)
  onDownloadFromBackend?: () => Promise<void>; // Backend download (backend embeds comments)
  initialComments?: CommentData[];
  originalBlob?: Blob; // Store original blob to preserve structure
  fileName?: string; // Current file name
  fileId?: number | string; // File ID for saving comments to API
  downloading?: boolean; // Loading state for download button
  onSaveComment?: (commentData: {
    file_id: number | string;
    file_name?: string; // File name for the payload
    paragraphIndex: number;
    paragraphText: string; // Full paragraph text for server-side validation
    startOffset: number;
    endOffset: number;
    selectedText: string;
    commentText: string;
    author: string;
    initials: string;
  }) => Promise<void>; // Callback to save comment to API
  onCommentDeleted?: (payload?: {
    commentId?: number | string;
    fileId?: number | string;
    commentText?: string;
    paragraphIndex?: number;
  }) => Promise<void>; // Callback to reload comments after deletion
  onCommentUpdated?: (commentId: string, updates: { 
    text: string;
    author?: string;
    initials?: string;
    fileName?: string;
    paragraphIndex?: number;
    startOffset?: number;
    endOffset?: number;
    selectedText?: string;
  }) => Promise<void>; // Callback to update comment text
  /** When true, document is view-only (no comments, edits, or downloads). */
  readOnly?: boolean;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  docxBlob,
  onDownload,
  onDownloadFromBackend,
  initialComments = [],
  originalBlob,
  fileName,
  fileId,
  downloading = false,
  onSaveComment,
  onCommentDeleted,
  onCommentUpdated,
  readOnly = false,
}) => {
  const canEditMockSurvey =
    !readOnly && canUpdateByName(FEATURE_NAMES.MOCK_SURVEY);
  const containerRef = useRef<HTMLDivElement>(null);
  const docxHostRef = useRef<HTMLDivElement>(null);
  // Initialize comments as empty - will be loaded from DOCX file on mount
  const [comments, setComments] = useState<CommentData[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<{ x: number; y: number; selectedText?: string; range?: Range | null } | null>(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const showCommentPanel = true;
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const [isDocumentRendered, setIsDocumentRendered] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [savingCommentId, setSavingCommentId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState<string>('');
  const documentContentRef = useRef<string>('');
  const originalBlobRef = useRef<Blob>(originalBlob || docxBlob);
  const commentMarkerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // New state for embedded comment overview
  const showCommentOverview = true;
  const showAllHighlights = true;
  const [expandOverview, setExpandOverview] = useState(false);
  
  // Update original blob ref when docxBlob changes
  useEffect(() => {
    if (originalBlob) {
      originalBlobRef.current = originalBlob;
    } else {
      originalBlobRef.current = docxBlob;
    }
  }, [docxBlob, originalBlob]);

  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        return name || user.name || user.email || 'Anonymous';
      }
    } catch (e) {
      // ignore
    }
    return 'Anonymous';
  };

  const getUserInitials = () => {
    const name = getCurrentUser();
    if (name && name !== 'Anonymous' && !name.includes('@')) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    return 'AN';
  };

  // Load comments from DOCX file
  const loadCommentsFromDocx = useCallback(async (blob: Blob) => {
    try {
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(await blob.arrayBuffer());
      
      // Check if comments.xml exists
      const commentsFile = zip.file('word/comments.xml');
      if (!commentsFile) {
        return []; // No comments in file
      }

      const commentsXml = await commentsFile.async('string');
      const parser = new DOMParser();
      const commentsDoc = parser.parseFromString(commentsXml, 'text/xml');
      const commentsRoot = commentsDoc.getElementsByTagName('w:comments')[0];
      
      if (!commentsRoot) {
        return [];
      }

      const loadedComments: CommentData[] = [];
      const repliesMap: Map<string, CommentData[]> = new Map(); // Map of parent comment ID to replies
      const commentElements = commentsRoot.getElementsByTagName('w:comment');
      const commentIdMap: Map<string, string> = new Map(); // Map DOCX comment ID to our comment ID
      
      // First pass: Load all comments and identify replies
      for (let i = 0; i < commentElements.length; i++) {
        const commentEl = commentElements[i] as Element;
        const commentId = commentEl.getAttribute('w:id') || String(i);
        const parentId = commentEl.getAttribute('w:parentId'); // Check if this is a reply
        const author = commentEl.getAttribute('w:author') || 'Unknown';
        const dateStr = commentEl.getAttribute('w:date') || '';
        
        // Extract comment text and selected text
        const paragraphs = commentEl.getElementsByTagName('w:p');
        let commentText = '';
        let selectedText = '';
        
        for (let k = 0; k < paragraphs.length; k++) {
          const para = paragraphs[k];
          const paraText = para.textContent || '';
          
          // Check if this paragraph contains selected text
          if (paraText.startsWith('Selected: ')) {
            selectedText = paraText.replace('Selected: ', '').trim();
          } else {
            // This is regular comment text, but filter out patterns like "policySelected: POLICY"
            const filteredText = paraText.trim();
            // Skip if it matches pattern like "policySelected: POLICY" or similar
            if (!filteredText.match(/^[a-zA-Z]+Selected:\s*/i)) {
              if (commentText) {
                commentText += ' ' + filteredText;
              } else {
                commentText = filteredText;
              }
            }
          }
        }
        
        // Fallback: if no comment text found in paragraphs, extract from all text elements
        if (!commentText.trim()) {
          const textElements = commentEl.getElementsByTagName('w:t');
          for (let j = 0; j < textElements.length; j++) {
            const textNode = textElements[j];
            if (textNode.textContent) {
              const text = textNode.textContent.trim();
              // Check for selected text in text elements too
              if (text.startsWith('Selected: ')) {
                if (!selectedText) {
                  selectedText = text.replace('Selected: ', '').trim();
                }
              } else if (!text.match(/^[a-zA-Z]+Selected:\s*/i)) {
                // Filter out "Selected: " patterns and "policySelected: " patterns
                commentText += text;
              }
            }
          }
        }
        
        // Also check if selectedText wasn't found in paragraphs, try to find it in all text elements
        if (!selectedText) {
          const allTextElements = commentEl.getElementsByTagName('w:t');
          for (let j = 0; j < allTextElements.length; j++) {
            const textNode = allTextElements[j];
            if (textNode.textContent) {
              const text = textNode.textContent.trim();
              if (text.startsWith('Selected: ')) {
                selectedText = text.replace('Selected: ', '').trim();
                break;
              }
            }
          }
        }

        if (commentText.trim()) {
          const ourCommentId = `docx-comment-${commentId}-${i}`;
          commentIdMap.set(commentId, ourCommentId);
          
          const commentData: CommentData = {
            id: ourCommentId,
            x: 0, // Will be positioned when document renders
            y: 0,
            text: commentText.trim(),
            author: author,
            timestamp: dateStr ? new Date(dateStr) : new Date(),
            selectedText: selectedText || undefined,
            docxCommentId: commentId, // Store original DOCX comment ID
          };
          
          if (parentId) {
            // This is a reply - store it for later attachment to parent
            if (!repliesMap.has(parentId)) {
              repliesMap.set(parentId, []);
            }
            repliesMap.get(parentId)!.push(commentData);
          } else {
            // This is a top-level comment
            loadedComments.push(commentData);
          }
        }
      }
      
      // Second pass: Attach replies to their parent comments
      loadedComments.forEach(comment => {
        if (comment.docxCommentId && repliesMap.has(comment.docxCommentId)) {
          comment.replies = repliesMap.get(comment.docxCommentId)!;
          // Set parentId for replies
          comment.replies.forEach(reply => {
            reply.parentId = comment.id;
          });
        }
      });

      return loadedComments;
    } catch (error) {
      console.warn('Failed to load comments from DOCX:', error);
      return [];
    }
  }, []);

  // Helper function to add pagination styles and preserve original document styles
  const addPaginationStyles = useCallback((container: HTMLElement) => {
    // Add CSS for page-like styling and preserve original styles if not already added
    if (!document.getElementById('docx-pagination-styles')) {
      const style = document.createElement('style');
      style.id = 'docx-pagination-styles';
      style.textContent = `
        /* Pagination styles */
        .docx-wrapper > section,
        .docx > section,
        section.docx-section {
          background: white !important;
          width: 8.5in !important;
          min-height: 11in !important;
          margin: 0 auto 24px auto !important;
          padding: 1in !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          page-break-after: always !important;
          page-break-inside: avoid !important;
          position: relative !important;
          overflow: visible !important;
        }
        .docx-wrapper > section:last-child,
        .docx > section:last-child,
        section.docx-section:last-child {
          margin-bottom: 0 !important;
        }
        
        /* Preserve table borders and styles */
        .docx table,
        .docx-wrapper table {
          border-collapse: collapse !important;
          border-spacing: 0 !important;
          width: 100% !important;
          margin: 12px 0 !important;
        }
        
        .docx table td,
        .docx table th,
        .docx-wrapper table td,
        .docx-wrapper table th {
          border: 1px solid #000 !important;
          padding: 4px 8px !important;
          vertical-align: top !important;
        }
        
        .docx table th,
        .docx-wrapper table th {
          background-color: #f0f0f0 !important;
          font-weight: bold !important;
        }
        
        /* Preserve all fonts and styles from original document */
        .docx *,
        .docx-wrapper * {
          font-family: inherit !important;
        }
        
        /* Ensure images are preserved */
        .docx img,
        .docx-wrapper img {
          max-width: 100% !important;
          height: auto !important;
        }
        
        @media (max-width: 1200px) {
          .docx-wrapper > section,
          .docx > section,
          section.docx-section {
            width: 100% !important;
            max-width: 8.5in !important;
            padding: 0.75in !important;
          }
        }
        @media (max-width: 768px) {
          .docx-wrapper > section,
          .docx > section,
          section.docx-section {
            padding: 0.5in !important;
          }
        }
        @media print {
          .docx-wrapper > section,
          .docx > section,
          section.docx-section {
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: always !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Check if docx-preview rendered sections and add pagination
    setTimeout(() => {
      const sections = container.querySelectorAll('section');
      if (sections.length > 0) {
        sections.forEach((section) => {
          section.classList.add('docx-section');
        });
      } else {
        // If no sections, check if we need to create page containers
        const wrapper = container.querySelector('.docx-wrapper') || container.querySelector('.docx');
        if (wrapper && wrapper.children.length > 0) {
          // docx-preview might have rendered without sections, try to detect pages
          // This is a fallback - docx-preview should handle pagination
        }
      }
      
      // Ensure table borders are visible
      const tables = container.querySelectorAll('table');
      tables.forEach((table) => {
        const cells = table.querySelectorAll('td, th');
        cells.forEach((cell) => {
          const htmlCell = cell as HTMLElement;
          if (!htmlCell.style.border || htmlCell.style.border === 'none') {
            htmlCell.style.border = '1px solid #000';
          }
        });
      });
    }, 200);
  }, []);

  const loadDocx = useCallback(async () => {
    if (!docxHostRef.current) return;

    const loadScript = (src: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
      });

    // Tag paragraphs with data-p-index after rendering
    /**
     * Returns all document-body <p> elements in order (including table cells).
     * Excludes <p> inside <header>/<footer> elements — docx-preview renders
     * those separately and they are not part of python-docx's paragraph list.
     * The backend uses body.iter(qn('w:p')) which also skips headers/footers.
     */
    const getDocumentParagraphElements = (host: HTMLElement): Element[] => {
      return Array.from(host.querySelectorAll('p')).filter(
        p => !p.closest('header') && !p.closest('footer')
      );
    };

    const tagParagraphs = (container: HTMLElement) => {
      // Clear any existing data-p-index attributes to avoid duplicates.
      container.querySelectorAll('[data-p-index]').forEach(el => {
        delete (el as HTMLElement).dataset.pIndex;
      });

      // Tag ALL <p> elements (including empty ones and table cells).
      // The backend iterates all <w:p> elements in document order via body.iter(),
      // so every <p> gets a sequential index matching the backend's enumeration.
      const allParagraphs = getDocumentParagraphElements(container);

      allParagraphs.forEach((p, index) => {
        (p as HTMLElement).dataset.pIndex = String(index);
      });

      console.log(`Tagged ${allParagraphs.length} paragraphs with data-p-index`);
    };

    try {
      const arrayBuffer = await docxBlob.arrayBuffer();

      // Try docx-preview first (npm package)
      try {
        const mod: any = await import('docx-preview').catch(() => null);
        
        if (mod && typeof mod.renderAsync === 'function') {
          try {
            const renderOptions: any = {
              inWrapper: true,
              className: 'docx',
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              renderHeaders: false,
              renderFooters: false,
            };
            
            await mod.renderAsync(arrayBuffer, docxHostRef.current, docxHostRef.current, renderOptions);
            
            // Mark document as rendered immediately after render completes
            setIsDocumentRendered(true);
            
            // Add pagination styling and preserve styles after render
            if (docxHostRef.current) {
              setTimeout(() => {
                addPaginationStyles(docxHostRef.current!);
                // Tag paragraphs with data-p-index after rendering
                tagParagraphs(docxHostRef.current!);
              }, 300);
            }
            return;
          } catch (renderError) {
            console.warn('docx-preview npm render failed, trying CDN', renderError);
            // Don't return here, let it fall through to CDN or mammoth
          }
        }
      } catch (e) {
        console.warn('docx-preview npm import failed, trying CDN', e);
      }

      // Try CDN fallback with proper initialization
      try {
        // Load docx-preview from CDN
        await loadScript('https://unpkg.com/docx-preview@0.3.1/dist/docx-preview.min.js');
        
        // Wait a bit for the library to initialize
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Try different ways to access the library
        let docxLib: any = (window as any).docx || (window as any).docxPreview;
        
        // If still not found, check for global renderAsync
        if (!docxLib && typeof (window as any).renderAsync === 'function') {
          docxLib = { renderAsync: (window as any).renderAsync };
        }
        
        if (docxLib && typeof docxLib.renderAsync === 'function') {
          try {
            // Use minimal options for CDN version to avoid compatibility issues
            const renderOptions: any = {
              inWrapper: true,
              className: 'docx',
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              renderHeaders: false,
              renderFooters: false,
            };
            
            await docxLib.renderAsync(arrayBuffer, docxHostRef.current, docxHostRef.current, renderOptions);
            
            // Mark document as rendered immediately after render completes
            setIsDocumentRendered(true);
            
            // Add pagination styling and preserve styles after render
            if (docxHostRef.current) {
              setTimeout(() => {
                addPaginationStyles(docxHostRef.current!);
                // Tag paragraphs with data-p-index after rendering
                tagParagraphs(docxHostRef.current!);
              }, 300);
            }
            return;
          } catch (renderError) {
            console.warn('docx-preview render failed, trying mammoth', renderError);
            // Don't return here, let it fall through to mammoth
          }
        } else {
          console.warn('docx-preview library not found after CDN load');
        }
      } catch (e) {
        console.warn('CDN docx-preview failed, trying mammoth', e);
      }

      // Fallback to mammoth
      try {
        const mammoth: any = await import('mammoth').catch(() => null);

        if (mammoth && typeof mammoth.convertToHtml === 'function') {
          const result = await mammoth.convertToHtml(
            { arrayBuffer },
            {
              styleMap: [
                "p[style-name='Heading 1'] => h1:fresh",
                "p[style-name='Heading 2'] => h2:fresh",
                "p[style-name='Heading 3'] => h3:fresh",
              ],
              includeDefaultStyleMap: true,
              ignoreEmptyParagraphs: false,
            }
          );
          const htmlContent = result.value || '<div/>';
          documentContentRef.current = htmlContent;
          if (docxHostRef.current) {
            docxHostRef.current.innerHTML = htmlContent;
            // Mark document as rendered immediately after setting innerHTML
            setIsDocumentRendered(true);
            // Add pagination styling for mammoth-rendered content
            setTimeout(() => {
              if (docxHostRef.current) {
                addPaginationStyles(docxHostRef.current);
                // Tag paragraphs with data-p-index after rendering
                tagParagraphs(docxHostRef.current);
              }
            }, 100);
          } else {
            setIsDocumentRendered(true);
          }
          return;
        }
      } catch (e) {
        console.warn('mammoth import failed, trying CDN', e);
      }

      // Mammoth CDN fallback
      try {
        await loadScript('https://unpkg.com/mammoth@1.6.0/mammoth.browser.min.js');
        const mammothLib: any = (window as any).mammoth;
        if (mammothLib && typeof mammothLib.convertToHtml === 'function') {
          const result = await mammothLib.convertToHtml(
            { arrayBuffer },
            {
              styleMap: [
                "p[style-name='Heading 1'] => h1:fresh",
                "p[style-name='Heading 2'] => h2:fresh",
                "p[style-name='Heading 3'] => h3:fresh",
              ],
              includeDefaultStyleMap: true,
              ignoreEmptyParagraphs: false,
            }
          );
          const htmlContent = result.value || '<div/>';
          documentContentRef.current = htmlContent;
          if (docxHostRef.current) {
            docxHostRef.current.innerHTML = htmlContent;
            // Mark document as rendered immediately after setting innerHTML
            setIsDocumentRendered(true);
            // Add pagination styling for mammoth-rendered content
            setTimeout(() => {
              if (docxHostRef.current) {
                addPaginationStyles(docxHostRef.current);
                // Tag paragraphs with data-p-index after rendering
                tagParagraphs(docxHostRef.current);
              }
            }, 100);
          } else {
            setIsDocumentRendered(true);
          }
          return;
        }
      } catch (e) {
        console.error('All docx rendering methods failed', e);
      }
    } catch (error) {
      console.error('Failed to load DOCX', error);
      if (docxHostRef.current) {
        docxHostRef.current.innerHTML = '<p>Failed to load document. Please try again.</p>';
      }
      setIsDocumentRendered(true); // Set even on error so positioning can still attempt
    }
  }, [docxBlob, addPaginationStyles]);

  // Draw / redraw absolute-position highlight overlays for every comment that has a range.
  // This runs whenever comments change, positions update, or a comment is highlighted.
  useEffect(() => {
    const docxHost = docxHostRef.current;
    if (!docxHost) return;

    // Remove all previous overlays.
    docxHost.querySelectorAll('[data-comment-highlight-overlay]').forEach(el => el.remove());

    if (!showAllHighlights) return;

    const hostRect = docxHost.getBoundingClientRect();

    comments.forEach((comment) => {
      if (!comment.range) return;

      let rects: DOMRect[];
      try {
        rects = Array.from(comment.range.getClientRects());
      } catch (_) {
        return;
      }

      rects.forEach((rect) => {
        if (rect.width <= 0 || rect.height <= 0) return;

        const overlay = document.createElement('span');
        overlay.setAttribute('data-comment-highlight-overlay', 'true');
        overlay.setAttribute('data-comment-id', comment.id);
        overlay.style.position = 'absolute';
        overlay.style.left = `${rect.left - hostRect.left}px`;
        overlay.style.top = `${rect.top - hostRect.top}px`;
        overlay.style.width = `${rect.width}px`;
        overlay.style.height = `${rect.height}px`;
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '2';
        overlay.style.borderRadius = '2px';
        overlay.style.backgroundColor =
          highlightedCommentId === comment.id
            ? 'rgba(255, 193, 7, 0.65)'
            : 'rgba(255, 235, 59, 0.45)';
        overlay.style.borderBottom =
          highlightedCommentId === comment.id
            ? '2px solid rgba(245, 124, 0, 0.9)'
            : '2px solid rgba(255, 152, 0, 0.6)';

        docxHost.appendChild(overlay);
      });
    });
  }, [comments, highlightedCommentId, showAllHighlights, isDocumentRendered]);

  // Load comments from DOCX when blob changes
  // Load comments from DOCX file whenever docxBlob changes or component mounts
  useEffect(() => {
    // Reset document rendered state when blob changes
    setIsDocumentRendered(false);
    
    // Cleanup existing highlights when document changes
    if (docxHostRef.current) {
      const highlights = docxHostRef.current.querySelectorAll('[data-comment-highlight]');
      highlights.forEach(highlight => highlight.remove());
    }
    
    const loadEmbeddedComments = async () => {
      if (!docxBlob) {
        // If no blob, use initialComments as fallback
        if (initialComments.length > 0) {
          console.log('No blob, using initialComments:', initialComments.length);
          setComments(initialComments);
        } else {
          setComments([]);
        }
        return;
      }
      
      // Check if we have API comments (from backend)
      const apiComments = initialComments.filter(c => c.id.startsWith('api-comment-'));
      
      // If API comments exist, use only those (don't load DOCX comments)
      if (apiComments.length > 0) {
        console.log('✅ Using API comments from backend (skipping DOCX comments):', apiComments.length);
        setComments(apiComments);
        return;
      }
      
      // Do not load or display embedded comments from DOCX in the right-side panel
      console.log('ℹ️ No API comments available, not loading embedded comments from DOCX for display');
      setComments([]);
    };

    // Always load comments when component mounts or docxBlob/initialComments changes
    loadEmbeddedComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docxBlob, loadCommentsFromDocx, fileName, initialComments]);

  useEffect(() => {
    setIsDocumentRendered(false); // Reset when document changes
    loadDocx();
  }, [loadDocx]);
  
  // Force positioning check when comments change and have invalid positions
  useEffect(() => {
    // This effect specifically handles the case when user navigates back to a file with API comments
    const apiCommentsNeedingPosition = comments.filter(c => 
      c.id.startsWith('api-comment-') && c.x === 0 && c.y === 0
    );
    
    if (apiCommentsNeedingPosition.length === 0) {
      return; // No API comments need positioning
    }
    
    console.log('🔄 Detected API comments needing position after comment state change:', apiCommentsNeedingPosition.length);
    
    // Force a positioning attempt after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (docxHostRef.current) {
        const hasContent = docxHostRef.current.textContent && docxHostRef.current.textContent.trim().length > 0;
        if (hasContent) {
          console.log('🔄 Forcing positioning attempt for API comments after navigation...');
          // Trigger a re-render by setting isDocumentRendered to true if it's false
          if (!isDocumentRendered) {
            setIsDocumentRendered(true);
          }
        }
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [comments, isDocumentRendered]);

  // Position existing comments after document is rendered
  useEffect(() => {
    console.log('📍 Position comments useEffect triggered:', {
      hasDocxHost: !!docxHostRef.current,
      commentsCount: comments.length,
      isDocumentRendered,
      commentsNeedingPosition: comments.filter(c => c.x === 0 && c.y === 0).map(c => ({ 
        id: c.id, 
        selectedText: c.selectedText,
        paragraphIndex: c.paragraphIndex,
        paragraphText: c.contextText?.substring(0, 80)
      }))
    });

    // Wait for both document to be rendered and comments to be loaded
    if (!docxHostRef.current || comments.length === 0) {
      // If document is not ready yet, return early
      console.log('⏳ Document not ready yet for positioning (no docxHost or no comments):', {
        hasDocxHost: !!docxHostRef.current,
        commentsCount: comments.length,
        isDocumentRendered
      });
      return;
    }
    
    // Check docxHost has actual content
    const docxHost = docxHostRef.current;
    const hasContent = docxHost.textContent && docxHost.textContent.trim().length > 0;
    const hasElements = docxHost.querySelectorAll('p, div').length > 0;
    
    console.log('📄 Document content check:', {
      hasContent,
      hasElements,
      textLength: docxHost.textContent?.length || 0,
      elementCount: docxHost.querySelectorAll('p, div').length
    });
    
    // Don't wait for isDocumentRendered if we have API comments - try positioning anyway
    const hasApiComments = comments.some(c => c.id.startsWith('api-comment-'));
    if (!isDocumentRendered && !hasApiComments) {
      console.log('⏳ Document not marked as rendered yet (no API comments)');
      return;
    }
    
    // If we have API comments and content, proceed even if not officially "rendered"
    if (hasApiComments && (!hasContent || !hasElements)) {
      console.log('⏳ Document has no content yet, waiting...');
      return;
    }

    // Check if there are any comments that need positioning (x:0, y:0 from DOCX or API)
    const needsPositioning = comments.some(c => 
      (c.x === 0 && c.y === 0) && 
      (c.id.startsWith('docx-comment-') || c.id.startsWith('api-comment-'))
    );
    if (!needsPositioning) {
      console.log('✅ All comments already positioned');
      return; // All comments already positioned
    }
    
    console.log('🚀 Starting comment positioning process... (API comments:', hasApiComments, ')');

    // Helper function to calculate text similarity (simple approach)
    const calculateSimilarity = (text1: string, text2: string): number => {
      if (text1 === text2) return 1.0;
      if (text1.length === 0 || text2.length === 0) return 0.0;
      
      // Use a simpler approach: check how many characters match
      const shorter = text1.length < text2.length ? text1 : text2;
      const longer = text1.length < text2.length ? text2 : text1;
      
      // If shorter text is contained in longer text, that's a good match
      if (longer.includes(shorter)) {
        return shorter.length / longer.length;
      }
      
      // Count matching characters at the beginning
      let matchCount = 0;
      const minLength = Math.min(text1.length, text2.length);
      for (let i = 0; i < minLength; i++) {
        if (text1[i] === text2[i]) {
          matchCount++;
        }
      }
      
      // Also check for substring matches
      const substringMatch = longer.indexOf(shorter.substring(0, Math.min(50, shorter.length)));
      if (substringMatch !== -1) {
        return 0.85; // Good match if we find a substantial substring
      }
      
      return matchCount / Math.max(text1.length, text2.length);
    };

    const isDocumentReady = (): boolean => {
      const docxHost = docxHostRef.current;
      if (!docxHost) {
        console.log('⏳ isDocumentReady: No docxHost');
        return false;
      }

      // Check if document has meaningful content (not just empty or whitespace)
      const hasContent = docxHost.children.length > 0 || (docxHost.textContent?.trim().length || 0) > 50;
      
      // Check if document has rendered elements (not just empty containers)
      const hasRenderedElements = docxHost.querySelectorAll('p, div, span, table, img, article, section').length > 0;
      
      // Check if document has a valid bounding rect (is visible)
      const rect = docxHost.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;

      const isReady = hasContent && hasRenderedElements && isVisible;
      
      if (!isReady) {
        console.log('⏳ isDocumentReady: Document not ready yet', {
          hasContent,
          hasRenderedElements,
          isVisible,
          childrenCount: docxHost.children.length,
          textLength: docxHost.textContent?.trim().length || 0,
          elementCount: docxHost.querySelectorAll('p, div, span, table, img, article, section').length
        });
      } else {
        console.log('✅ isDocumentReady: Document is ready');
      }

      return isReady;
    };

    const positionComments = () => {
      const docxHost = docxHostRef.current;
      if (!docxHost) return false;

      // Check if document is ready
      if (!isDocumentReady()) {
        return false; // Document not ready yet
      }

      // Get the correct container rect for positioning
      const docxRect = docxHost.getBoundingClientRect();
      
      let positionedCount = 0;
      
      // Check current comments to see how many need positioning
      const currentComments = comments;
      const needsPositioningCount = currentComments.filter(c => 
        (c.x === 0 && c.y === 0) && 
        (c.id.startsWith('docx-comment-') || c.id.startsWith('api-comment-'))
      ).length;
      
      if (needsPositioningCount === 0) {
        return true; // All comments already positioned
      }
      
      setComments(prevComments => {
        const updated = prevComments.map(comment => {
          // Only position comments that need positioning (x:0, y:0 from DOCX or API)
          if (!((comment.x === 0 && comment.y === 0) && 
                (comment.id.startsWith('docx-comment-') || comment.id.startsWith('api-comment-')))) {
            return comment;
          }

          // For API-loaded comments, use paragraphText, paragraphIndex, and offsets for accurate positioning
          if (comment.id.startsWith('api-comment-') && 
              comment.paragraphIndex !== undefined && 
              comment.paragraphIndex >= 0) {
            try {
              // Body <p> elements only (no header/footer) — matches backend's body.iter(w:p).
              const paragraphsArray: Element[] = Array.from(docxHost.querySelectorAll('p')).filter(
                p => !p.closest('header') && !p.closest('footer')
              );
              
              console.log(`🔍 Positioning API comment:`, {
                commentId: comment.id,
                paragraphIndex: comment.paragraphIndex,
                startOffset: comment.startOffset,
                endOffset: comment.endOffset,
                selectedText: comment.selectedText,
                paragraphText: comment.contextText?.substring(0, 100) + '...',
                hasContextText: !!comment.contextText,
                totalParagraphs: paragraphsArray.length,
                sampleParagraphs: paragraphsArray.slice(0, Math.min(10, paragraphsArray.length)).map((p, i) => ({
                  index: i,
                  text: (p.textContent || '').substring(0, 80) + '...'
                }))
              });
              
              let paragraphElement: Element | null = null;
              
              // Normalize function for consistent text matching
              const normalizeForMatching = (text: string): string => {
                // More aggressive normalization to handle various whitespace and formatting issues
                return text
                  .replace(/\u00A0/g, ' ')  // Non-breaking spaces
                  .replace(/\r\n/g, ' ')     // Windows line endings
                  .replace(/\r/g, ' ')       // Mac line endings
                  .replace(/\n/g, ' ')       // Unix line endings
                  .replace(/\s+/g, ' ')      // Multiple spaces to single space
                  .replace(/\u200B/g, '')    // Zero-width space
                  .replace(/\u200C/g, '')    // Zero-width non-joiner
                  .replace(/\u200D/g, '')    // Zero-width joiner
                  .replace(/\uFEFF/g, '')    // Zero-width no-break space
                  .trim();
              };
              
              // Helper to pick the best matching paragraph based on proximity to API paragraphIndex
              const pickBestByIndex = (candidates: Array<{ index: number; element: Element }>): Element | null => {
                if (!candidates.length) return null;
                if (comment.paragraphIndex === undefined || comment.paragraphIndex < 0) {
                  return candidates[0].element;
                }
                let best = candidates[0];
                let bestDistance = Math.abs(best.index - comment.paragraphIndex);
                for (let i = 1; i < candidates.length; i++) {
                  const candidate = candidates[i];
                  const distance = Math.abs(candidate.index - comment.paragraphIndex);
                  if (distance < bestDistance) {
                    best = candidate;
                    bestDistance = distance;
                  }
                }
                return best.element;
              };
              
              // PRIORITY 1: Find paragraph by matching paragraphText/context (most reliable for API comments)
              if (comment.contextText) {
                const normalizedContext = normalizeForMatching(comment.contextText);
                // Use first 300 chars for matching (long enough to be unique, short enough to avoid issues)
                const contextSnippet = normalizedContext.substring(0, Math.min(300, normalizedContext.length));
                
                // Also try matching with selectedText if available (for better accuracy)
                const normalizedSelected = comment.selectedText ? normalizeForMatching(comment.selectedText) : '';
                
                console.log(`🔍 Searching for paragraph with context: "${contextSnippet}"...`);
                console.log(`🔍 Searching for selectedText: "${normalizedSelected}"...`);
                
                const exactMatches: Array<{ index: number; element: Element }> = [];
                const snippetMatches: Array<{ index: number; element: Element }> = [];
                const selectedTextMatches: Array<{ index: number; element: Element }> = [];
                const fuzzyMatches: Array<{ index: number; element: Element }> = [];
                
                for (let i = 0; i < paragraphsArray.length; i++) {
                  const para = paragraphsArray[i];
                  const paraText = normalizeForMatching(para.textContent || '');
                  
                  // Check if paragraph text matches (allowing for some variation)
                  if (paraText.length > 0) {
                    // Try exact match first
                    if (paraText === normalizedContext) {
                      exactMatches.push({ index: i, element: para as Element });
                      continue;
                    }
                    
                    // Try matching with context snippet
                    if (paraText.includes(contextSnippet) || contextSnippet.includes(paraText.substring(0, Math.min(300, paraText.length)))) {
                      snippetMatches.push({ index: i, element: para as Element });
                      continue;
                    }
                    
                    // Try matching with selectedText within paragraph (more specific)
                    if (normalizedSelected && paraText.includes(normalizedSelected)) {
                      // Verify this is likely the right paragraph by checking if offsets make sense
                      const selectedIndex = paraText.indexOf(normalizedSelected);
                      if (selectedIndex >= 0 && selectedIndex < paraText.length) {
                        selectedTextMatches.push({ index: i, element: para as Element });
                        continue;
                      }
                    }
                    
                    // Try fuzzy matching - check if context is similar (80% match)
                    const similarity = calculateSimilarity(paraText, normalizedContext);
                    if (similarity > 0.8) {
                      fuzzyMatches.push({ index: i, element: para as Element });
                      continue;
                    }
                  }
                }
                
                if (!paragraphElement) {
                  // Prefer exact matches, then snippet, then selectedText, then fuzzy
                  let picked: Element | null = null;
                  if (exactMatches.length) {
                    picked = pickBestByIndex(exactMatches);
                    console.log('✅ Paragraph chosen from exact matches for API comment:', {
                      expectedIndex: comment.paragraphIndex,
                      chosenIndex: exactMatches.find(c => c.element === picked)?.index,
                    });
                  } else if (snippetMatches.length) {
                    picked = pickBestByIndex(snippetMatches);
                    console.log('✅ Paragraph chosen from snippet matches for API comment:', {
                      expectedIndex: comment.paragraphIndex,
                      chosenIndex: snippetMatches.find(c => c.element === picked)?.index,
                    });
                  } else if (selectedTextMatches.length) {
                    picked = pickBestByIndex(selectedTextMatches);
                    console.log('✅ Paragraph chosen from selectedText matches for API comment:', {
                      expectedIndex: comment.paragraphIndex,
                      chosenIndex: selectedTextMatches.find(c => c.element === picked)?.index,
                    });
                  } else if (fuzzyMatches.length) {
                    picked = pickBestByIndex(fuzzyMatches);
                    console.log('✅ Paragraph chosen from fuzzy matches for API comment:', {
                      expectedIndex: comment.paragraphIndex,
                      chosenIndex: fuzzyMatches.find(c => c.element === picked)?.index,
                    });
                  }
                  
                  if (picked) {
                    paragraphElement = picked;
                  }
                }
              }

              // Validate Priority 1 result: the found paragraph must actually contain
              // the selectedText. If it doesn't (backend stored wrong paragraphText),
              // reset so Priority 2 can find the correct paragraph.
              if (paragraphElement && comment.selectedText) {
                const foundParaText = normalizeForMatching(paragraphElement.textContent || '');
                const normalizedSel = normalizeForMatching(comment.selectedText);
                if (normalizedSel && !foundParaText.includes(normalizedSel)) {
                  console.warn('⚠️ Paragraph found by contextText does not contain selectedText — falling back to selectedText search.', {
                    expectedSelected: normalizedSel,
                    foundParaText: foundParaText.substring(0, 100),
                  });
                  paragraphElement = null; // force Priority 2
                }
              }
              
              // PRIORITY 2: Find by selectedText if paragraphText matching failed
              if (!paragraphElement && comment.selectedText) {
                const normalizedSelected = comment.selectedText.replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n').trim();
                const selectedCandidates: Array<{ index: number; element: Element }> = [];
                for (let i = 0; i < paragraphsArray.length; i++) {
                  const para = paragraphsArray[i];
                  const paraText = (para.textContent || '').replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n');
                  if (paraText.includes(normalizedSelected)) {
                    selectedCandidates.push({ index: i, element: para as Element });
                  }
                }
                if (selectedCandidates.length) {
                  const picked = pickBestByIndex(selectedCandidates);
                  if (picked) {
                    paragraphElement = picked;
                    const chosenIndex = selectedCandidates.find(c => c.element === picked)?.index;
                    console.log('✅ Paragraph chosen from selectedText-only matches for API comment:', {
                      expectedIndex: comment.paragraphIndex,
                      chosenIndex,
                    });
                  }
                }
              }
              
              // PRIORITY 3: Try data attributes
              if (!paragraphElement) {
                const paragraphId = `p-${comment.paragraphIndex}`;
                paragraphElement = docxHost.querySelector(`[data-paragraph-id="${paragraphId}"]`) ||
                                   docxHost.querySelector(`[data-paragraph-id="${comment.paragraphIndex}"]`) ||
                                   docxHost.querySelector(`[data-p-index="${comment.paragraphIndex}"]`);
                if (paragraphElement) {
                  console.log(`✅ Found paragraph using data attribute: ${paragraphId}`);
                }
              }
              
              // PRIORITY 4: Use index as last resort
              if (!paragraphElement && paragraphsArray[comment.paragraphIndex]) {
                paragraphElement = paragraphsArray[comment.paragraphIndex] as Element;
                console.log(`✅ Found paragraph by index: ${comment.paragraphIndex} (total paragraphs: ${paragraphsArray.length})`);
              }
              
              if (!paragraphElement) {
                console.warn(`⚠️ Could not find paragraph for API comment:`, {
                  commentId: comment.id,
                  paragraphIndex: comment.paragraphIndex,
                  totalParagraphs: paragraphsArray.length,
                  hasContextText: !!comment.contextText,
                  hasSelectedText: !!comment.selectedText
                });
                return comment; // Return unchanged if paragraph not found
              }
              
              // Normalize paragraph text for consistent offset calculation
              // Important: Use the EXACT SAME normalization as when saving the comment (handleAddComment)
              const normalizeText = (text: string): string => {
                return text.replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
              };
              
              const paraText = normalizeText(paragraphElement.textContent || '');
              
              // Use startOffset and endOffset as working variables
              let startOffset = -1;
              let endOffset = -1;

              // Highest priority for UI positioning: if we know the paragraphIndex, occurrence,
              // and selectedText, re-compute offsets in THIS rendered paragraph using the
              // nth occurrence logic (so we don't accidentally anchor to the first occurrence).
              if (
                comment.selectedText &&
                comment.selectedText.trim() &&
                comment.paragraphIndex !== undefined &&
                comment.occurrence !== undefined &&
                comment.occurrence > 0
              ) {
                const normalizedPara = paraText.toLowerCase();
                const normalizedSearch = normalizeText(comment.selectedText.trim()).toLowerCase();

                let count = 0;
                let searchIndex = 0;
                const occurrencePositions: Array<{ start: number; end: number }> = [];

                while (true) {
                  const foundIndex = normalizedPara.indexOf(normalizedSearch, searchIndex);
                  if (foundIndex === -1) break;

                  count++;
                  occurrencePositions.push({
                    start: foundIndex,
                    end: foundIndex + normalizedSearch.length,
                  });

                  searchIndex = foundIndex + 1;
                }

                if (comment.occurrence <= count) {
                  const target = occurrencePositions[comment.occurrence - 1];
                  startOffset = target.start;
                  endOffset = target.end;
                  console.log('📍 Using occurrence-based offsets for UI marker:', {
                    paragraphIndex: comment.paragraphIndex,
                    occurrence: comment.occurrence,
                    startOffset,
                    endOffset,
                    paraLength: paraText.length,
                    selectedText: comment.selectedText,
                  });
                } else {
                  console.warn('⚠️ Occurrence out of range for UI marker, falling back to API offsets/context:', {
                    paragraphIndex: comment.paragraphIndex,
                    requestedOccurrence: comment.occurrence,
                    foundOccurrences: count,
                    selectedText: comment.selectedText,
                  });
                }
              }

              // Next priority: use stored offsets from API payload if we didn't already
              // determine offsets from occurrence-based logic.
              // ALWAYS prefer searching for selectedText in the paragraph first — the stored
              // offsets may have been calculated against a different paragraph (backend bug).
              if (startOffset === -1 && endOffset === -1) {
                const normalizedSelectedText = normalizeText(comment.selectedText || '');

                if (normalizedSelectedText.length > 0) {
                  // Search for selectedText anywhere in the paragraph — most reliable.
                  const searchIdx = paraText.indexOf(normalizedSelectedText);
                  if (searchIdx !== -1) {
                    startOffset = searchIdx;
                    endOffset = searchIdx + normalizedSelectedText.length;
                    console.log(`✅ Offsets derived from selectedText search: ${startOffset}–${endOffset}`);
                  }
                }

                // Only fall back to stored API offsets when selectedText search found nothing.
                if (startOffset === -1 && comment.startOffset !== undefined && comment.endOffset !== undefined) {
                  startOffset = comment.startOffset;
                  endOffset = comment.endOffset;
                  console.log(`📍 Using API offsets (fallback): startOffset=${startOffset}, endOffset=${endOffset}`);
                }
              }

              // Legacy block kept for backward compat — validates/corrects API offsets when
              // selectedText search above didn't run (occurrence-based path set the offsets).
              if (startOffset !== -1 && endOffset !== -1 && comment.startOffset !== undefined && comment.endOffset !== undefined) {
                const extractedText = paraText.substring(startOffset, endOffset);
                const normalizedSelectedText = normalizeText(comment.selectedText || '');

                if (normalizedSelectedText.length > 0) {
                  if (extractedText !== normalizedSelectedText) {
                    const searchIndex = paraText.indexOf(normalizedSelectedText);
                    if (searchIndex !== -1) {
                      console.log(`✅ Corrected mismatched offsets by re-searching selectedText`);
                      startOffset = searchIndex;
                      endOffset = searchIndex + normalizedSelectedText.length;
                    }
                  }
                } else {
                  // When selectedText is empty, validate that offsets are within bounds
                  // and ensure endOffset > startOffset
                  if (startOffset < 0 || endOffset <= startOffset || endOffset > paraText.length) {
                    console.warn(`⚠️ Invalid offsets for empty selectedText:`, {
                      startOffset,
                      endOffset,
                      paraLength: paraText.length,
                      extractedText
                    });
                    
                    // If offsets are invalid, try to use a default position (start of paragraph)
                    if (startOffset < 0 || endOffset <= startOffset) {
                      startOffset = 0;
                      endOffset = Math.min(1, paraText.length);
                      console.log(`✅ Corrected invalid offsets to: startOffset=${startOffset}, endOffset=${endOffset}`);
                    } else if (endOffset > paraText.length) {
                      endOffset = paraText.length;
                      startOffset = Math.max(0, endOffset - 1);
                      console.log(`✅ Corrected endOffset to paragraph length: startOffset=${startOffset}, endOffset=${endOffset}`);
                    }
                  } else {
                    console.log(`✅ Valid offsets for empty selectedText: startOffset=${startOffset}, endOffset=${endOffset}, extractedText="${extractedText}"`);
                  }
                }
              } else if (startOffset === -1 && endOffset === -1 && comment.beforeText !== undefined && comment.afterText !== undefined) {
                // Calculate offsets from beforeText length
                startOffset = normalizeText(comment.beforeText).length;
                endOffset = startOffset + normalizeText(comment.selectedText || '').length;
              } else if (startOffset === -1 && endOffset === -1 && comment.selectedText) {
                // Fallback: find selectedText in paragraph
                const normalizedSelectedText = normalizeText(comment.selectedText);
                const index = paraText.indexOf(normalizedSelectedText);
                if (index !== -1) {
                  startOffset = index;
                  endOffset = index + normalizedSelectedText.length;
                }
              }
              
              // Validate offsets against paragraph text length
              const maxOffset = paraText.length;
              const validStartOffset = Math.max(0, Math.min(startOffset, maxOffset));
              const validEndOffset = Math.max(validStartOffset, Math.min(endOffset, maxOffset));
              
              // Final validation: ensure the extracted text matches selectedText
              const finalExtractedText = paraText.substring(validStartOffset, validEndOffset);
              const finalSelectedText = normalizeText(comment.selectedText || '');
              if (finalExtractedText !== finalSelectedText && finalSelectedText.length > 0) {
                console.warn(`⚠️ Final offset validation failed:`, {
                  expected: finalSelectedText,
                  actual: finalExtractedText,
                  validStartOffset,
                  validEndOffset,
                  paraLength: paraText.length
                });
              }
              
              // If we have valid offsets, use them to create a range
              if (startOffset >= 0 && endOffset > startOffset && endOffset <= maxOffset) {
                // Use validated offsets
                const finalStartOffset = validStartOffset;
                const finalEndOffset = validEndOffset;
                
                console.log(`📍 Offset validation:`, {
                  originalOffsets: { startOffset, endOffset },
                  validOffsets: { validStartOffset, validEndOffset },
                  paraLength: maxOffset,
                  selectedText: comment.selectedText
                });
                
                try {
                    // Normalize function for consistent text processing (defined before use)
                    // MUST match the EXACT normalization used when saving comments (handleAddComment)
                    const normalizeNodeText = (text: string): string => {
                      return text.replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
                    };
                    
                    // Create range using TreeWalker to find the correct text node and offset
                    const walker = document.createTreeWalker(
                      paragraphElement as Node,
                      NodeFilter.SHOW_TEXT,
                      null
                    );
                    
                    let node: Node | null = null;
                    let cumulativeOffset = 0;
                    let startNode: Node | null = null;
                    let endNode: Node | null = null;
                    let startNodeOffset = 0;
                    let endNodeOffset = 0;
                    
                    while ((node = walker.nextNode()) !== null) {
                      const nodeText = normalizeNodeText(node.textContent || '');
                      const nodeLength = nodeText.length;
                      
                      // Check if finalStartOffset is within this node
                      if (startNode === null && cumulativeOffset + nodeLength >= finalStartOffset) {
                        startNode = node;
                        startNodeOffset = Math.max(0, Math.min(finalStartOffset - cumulativeOffset, nodeLength));
                      }
                      
                      // Check if finalEndOffset is within this node
                      if (endNode === null && cumulativeOffset + nodeLength >= finalEndOffset) {
                        endNode = node;
                        endNodeOffset = Math.max(0, Math.min(finalEndOffset - cumulativeOffset, nodeLength));
                        break;
                      }
                      
                      cumulativeOffset += nodeLength;
                    }
                    
                      // If we didn't find endNode but found startNode, use the last node
                      if (startNode && !endNode) {
                        // Find the last text node in the paragraph
                        const lastWalker = document.createTreeWalker(
                          paragraphElement as Node,
                          NodeFilter.SHOW_TEXT,
                          null
                        );
                        let lastNode: Node | null = null;
                        let lastNodeOffset = 0;
                        let lastCumulative = 0;
                        while ((lastNode = lastWalker.nextNode()) !== null) {
                          const lastNodeText = normalizeNodeText(lastNode.textContent || '');
                          lastCumulative += lastNodeText.length;
                          if (lastCumulative >= finalEndOffset) {
                            lastNodeOffset = Math.max(0, Math.min(finalEndOffset - (lastCumulative - lastNodeText.length), lastNodeText.length));
                            break;
                          }
                        }
                        if (lastNode) {
                          endNode = lastNode;
                          endNodeOffset = Math.max(0, lastNodeOffset);
                        }
                      }
                    
                    if (startNode && endNode) {
                      const range = document.createRange();
                      range.setStart(startNode, Math.max(0, startNodeOffset));
                      range.setEnd(endNode, Math.max(0, endNodeOffset));
                      
                      console.log('📍 Range created:', {
                        commentId: comment.id,
                        rangeText: range.toString(),
                        startNode: startNode.textContent?.substring(0, 50),
                        endNode: endNode.textContent?.substring(0, 50),
                        startNodeOffset,
                        endNodeOffset,
                        collapsed: range.collapsed
                      });
                      
                      const rangeRect = range.getBoundingClientRect();
                      
                      // Validate rangeRect - it must have valid dimensions and position
                      // Note: top/left can be 0 if element is at top-left, so check if rect has meaningful size
                      const isValidRect = (rangeRect.width > 0 || rangeRect.height > 0);
                      const hasValidPosition = (rangeRect.top !== 0 || rangeRect.left !== 0 || (rangeRect.width > 0 && rangeRect.height > 0));
                      
                      if (isValidRect && hasValidPosition) {
                        // Calculate position relative to the docxHost element (where markers are positioned)
                        const docxHostRect = docxHost.getBoundingClientRect();
                        const x = rangeRect.left + rangeRect.width / 2 - docxHostRect.left;
                        const y = rangeRect.top + rangeRect.height / 2 - docxHostRect.top;
                        
                        console.log(`📍 Calculated position for ${comment.id}:`, {
                          x,
                          y,
                          rangeRect: { left: rangeRect.left, top: rangeRect.top, width: rangeRect.width, height: rangeRect.height },
                          docxHostRect: { left: docxHostRect.left, top: docxHostRect.top }
                        });
                        
                        // Validate calculated position (allow wide margin for scrolling and document size)
                        const isValidPosition = !isNaN(x) && !isNaN(y) && isFinite(x) && isFinite(y) && 
                                               x >= -1000 && y >= -1000 && x <= docxHostRect.width + 1000 && y <= 50000;
                        
                        if (isValidPosition) {
                          console.log('✅ Valid position calculated for marker:', {
                            commentId: comment.id,
                            x,
                            y,
                            selectedText: comment.selectedText
                          });
                          
                          // IMPORTANT: Do NOT create DOM highlights to avoid hiding text
                          // Just position the marker - text remains untouched in the document
                          
                          positionedCount++;
                          return {
                            ...comment,
                            x: Math.max(0, x),
                            y: Math.max(0, y),
                            range: range,
                          };
                        } else {
                          console.warn('⚠️ Calculated position out of bounds:', { x, y, docxHostRect });
                        }
                      } else {
                        console.warn('⚠️ Invalid rangeRect for API comment:', {
                          commentId: comment.id,
                          rangeRect: { width: rangeRect.width, height: rangeRect.height, top: rangeRect.top, left: rangeRect.left },
                          isValidRect,
                          hasValidPosition
                        });
                        
                        // Fallback: use paragraph position if range positioning fails
                        const paraRect = paragraphElement.getBoundingClientRect();
                        if (paraRect.width > 0 && paraRect.height > 0) {
                          const docxHostRect = docxHost.getBoundingClientRect();
                          const fallbackX = paraRect.left + paraRect.width / 2 - docxHostRect.left;
                          const fallbackY = paraRect.top + paraRect.height / 2 - docxHostRect.top;
                          
                          if (!isNaN(fallbackX) && !isNaN(fallbackY) && isFinite(fallbackX) && isFinite(fallbackY)) {
                            console.log('✅ Using paragraph fallback position for API comment');
                            positionedCount++;
                            return {
                              ...comment,
                              x: Math.max(0, fallbackX),
                              y: Math.max(0, fallbackY),
                            };
                          }
                        }
                      }
                    }
                  } catch (e) {
                    console.warn('Error creating range from paragraphIndex for API comment:', e);
                  }
                } else {
                  console.warn('⚠️ No valid offsets found for API comment:', {
                    commentId: comment.id,
                    startOffset: startOffset,
                    endOffset: endOffset,
                    paraLength: paraText.length
                  });
                  return comment;
                }
            } catch (e) {
              console.warn('Error positioning API comment by paragraphIndex:', e);
              return comment;
            }
          }

          // Try to find the selectedText in the rendered document
          if (comment.selectedText && comment.selectedText.trim()) {
            const searchText = comment.selectedText.trim();
            
            // Search for text in the rendered HTML
            const walker = document.createTreeWalker(
              docxHost,
              NodeFilter.SHOW_TEXT,
              null
            );

            let node: Node | null = null;
            while ((node = walker.nextNode()) !== null) {
              const text = node.textContent || '';
              const normalizedText = text.toLowerCase();
              const normalizedSearch = searchText.toLowerCase();
              const index = normalizedText.indexOf(normalizedSearch);
              
              if (index !== -1) {
                try {
                  // Found the text, calculate position
                  const range = document.createRange();
                  range.setStart(node, index);
                  range.setEnd(node, index + searchText.length);
                  
                  const rangeRect = range.getBoundingClientRect();
                  
                  // Only use if rect is valid
                  if (rangeRect.width > 0 || rangeRect.height > 0) {
                    const docxHostRect = docxHost.getBoundingClientRect();
                    const x = rangeRect.left + rangeRect.width / 2 - docxHostRect.left;
                    const y = rangeRect.top + rangeRect.height / 2 - docxHostRect.top;
                    
                    positionedCount++;
                    return {
                      ...comment,
                      x: Math.max(0, x),
                      y: Math.max(0, y),
                    };
                  }
                } catch (e) {
                  // Range error, continue searching
                  console.warn('Error creating range for comment positioning:', e);
                }
              }
            }
          }

          // Try to find the selectedText in the rendered document
          if (comment.selectedText && comment.selectedText.trim()) {
            const searchText = comment.selectedText.trim();
            
            // Search for text in the rendered HTML
            const walker = document.createTreeWalker(
              docxHost,
              NodeFilter.SHOW_TEXT,
              null
            );

            let node: Node | null = null;
            while ((node = walker.nextNode()) !== null) {
              const text = node.textContent || '';
              const normalizedText = text.toLowerCase();
              const normalizedSearch = searchText.toLowerCase();
              const index = normalizedText.indexOf(normalizedSearch);
              
              if (index !== -1) {
                try {
                  // Found the text, calculate position
                  const range = document.createRange();
                  range.setStart(node, index);
                  range.setEnd(node, index + searchText.length);
                  
                  const rangeRect = range.getBoundingClientRect();
                  
                  // Only use if rect is valid
                  if (rangeRect.width > 0 || rangeRect.height > 0) {
                    const docxHostRect = docxHost.getBoundingClientRect();
                    const x = rangeRect.left + rangeRect.width / 2 - docxHostRect.left;
                    const y = rangeRect.top + rangeRect.height / 2 - docxHostRect.top;
                    
                    positionedCount++;
                    return {
                      ...comment,
                      x: Math.max(0, x),
                      y: Math.max(0, y),
                    };
                  }
                } catch (e) {
                  // Range error, continue searching
                  console.warn('Error creating range for comment positioning:', e);
                }
              }
            }
          }

          // Fallback: if we have comment text, try to find it in the document
          if (comment.text) {
            const walker = document.createTreeWalker(
              docxHost,
              NodeFilter.SHOW_TEXT,
              null
            );

            let node: Node | null = null;
            while ((node = walker.nextNode()) !== null) {
              const text = node.textContent || '';
              // Look for a portion of the comment text
              const commentWords = comment.text.trim().split(/\s+/).slice(0, 3);
              const searchPattern = commentWords.join(' ');
              
              if (text.toLowerCase().includes(searchPattern.toLowerCase())) {
                try {
                  const range = document.createRange();
                  const index = text.toLowerCase().indexOf(searchPattern.toLowerCase());
                  range.setStart(node, index);
                  range.setEnd(node, index + searchPattern.length);
                  
                  const rangeRect = range.getBoundingClientRect();
                  if (rangeRect.width > 0 || rangeRect.height > 0) {
                    const x = rangeRect.left + rangeRect.width / 2 - docxRect.left;
                    const y = rangeRect.top + rangeRect.height / 2 - docxRect.top;
                    
                    positionedCount++;
                    return {
                      ...comment,
                      x: Math.max(0, x),
                      y: Math.max(0, y),
                    };
                  }
                } catch (e) {
                  console.warn('Error creating range for comment positioning:', e);
                }
              }
            }
          }

          // Last resort: estimate position based on comment index
          const commentIndex = prevComments.findIndex(c => c.id === comment.id);
          const totalHeight = docxHost.scrollHeight || docxHost.offsetHeight || 1000;
          const estimatedY = (commentIndex + 1) * (totalHeight / (prevComments.length + 1));
          const estimatedX = docxRect.width / 2;
          
          positionedCount++;
          return {
            ...comment,
            x: estimatedX,
            y: Math.max(50, estimatedY),
          };
        });

        return updated;
      });

      // Return true if at least one comment was positioned, or if no comments need positioning
      return positionedCount > 0 || needsPositioningCount === 0;
    };

    // Retry mechanism with increasing delays
    let retryCount = 0;
    const maxRetries = 15;
    const retryDelays = [100, 300, 500, 800, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000, 6000, 7000, 8000];
    let timeoutIds: NodeJS.Timeout[] = [];

    const tryPositionComments = () => {
      // Check if document is ready first
      if (!isDocumentReady()) {
        if (retryCount < maxRetries) {
          retryCount++;
          const delay = retryDelays[Math.min(retryCount - 1, retryDelays.length - 1)];
          const timeoutId = setTimeout(tryPositionComments, delay);
          timeoutIds.push(timeoutId);
        }
        return;
      }

      // Try to position comments
      const success = positionComments();
      
      // Check if more retries are needed after a short delay to allow state update
      if (!success && retryCount < maxRetries) {
        retryCount++;
        const delay = retryDelays[Math.min(retryCount - 1, retryDelays.length - 1)];
        const timeoutId = setTimeout(() => {
          // Check if comments still need positioning (both DOCX and API comments)
          setComments(currentComments => {
            const stillNeedsPositioning = currentComments.some(c => 
              (c.x === 0 && c.y === 0) && 
              (c.id.startsWith('docx-comment-') || c.id.startsWith('api-comment-'))
            );
            if (stillNeedsPositioning) {
              console.log(`🔄 Retrying comment positioning (attempt ${retryCount}/${maxRetries})...`);
              tryPositionComments();
            }
            return currentComments;
          });
        }, delay);
        timeoutIds.push(timeoutId);
      }
    };

    // Start positioning attempts - wait for document to be fully rendered
    const hasApiCommentsToPosition = comments.some(c => c.id.startsWith('api-comment-'));
    const initialDelay = hasApiCommentsToPosition ? 200 : 100; // Shorter initial delay
    
    const timeoutId = setTimeout(() => {
      retryCount = 0; // Reset retry count for this attempt
      console.log('🚀 Starting comment positioning (initial delay)...', {
        totalComments: comments.length,
        delay: initialDelay,
        apiComments: comments.filter(c => c.id.startsWith('api-comment-')).length,
        docxComments: comments.filter(c => c.id.startsWith('docx-comment-')).length
      });
      tryPositionComments();
    }, initialDelay);
    timeoutIds.push(timeoutId);

    // Also try after additional delays in case document takes longer to fully settle
    [500, 1000, 1500, 2000, 3000, 4000].forEach((delay, index) => {
      const timeoutId = setTimeout(() => {
        retryCount = 0; // Reset retry count for this attempt
        console.log(`⏰ Additional positioning attempt ${index + 1} (${delay}ms delay)`);
        tryPositionComments();
      }, delay);
      timeoutIds.push(timeoutId);
    });

    // Use MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      if (isDocumentReady()) {
        // Check current comments state
        setComments(currentComments => {
          const stillNeedsPositioning = currentComments.some(c => 
            c.x === 0 && c.y === 0 && 
            (c.id.startsWith('docx-comment-') || c.id.startsWith('api-comment-'))
          );
          if (stillNeedsPositioning) {
            console.log('🔄 DOM changed, retrying positioning for comments needing position');
            retryCount = 0; // Reset retry count when DOM changes
            tryPositionComments();
          }
          return currentComments;
        });
      }
    });

    if (docxHostRef.current) {
      observer.observe(docxHostRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
      timeoutIds = [];
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments.length, docxBlob, isDocumentRendered]);

  // Helper function to find word at click position
  const findWordAtPosition = (clickX: number, clickY: number, docxHost: HTMLElement): { text: string; range: Range | null } => {
    try {
      // Try to get range at click position using caretRangeFromPoint (standard method)
      let range: Range | null = null;
      
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(clickX, clickY);
      } else if ((document as any).caretPositionFromPoint) {
        // Firefox fallback
        const caretPos = (document as any).caretPositionFromPoint(clickX, clickY);
        if (caretPos) {
          range = document.createRange();
          range.setStart(caretPos.offsetNode, caretPos.offset);
          range.setEnd(caretPos.offsetNode, caretPos.offset);
        }
      }
      
      // If we have a range, find the word boundaries
      if (range) {
        const textNode = range.startContainer;
        if (textNode.nodeType === Node.TEXT_NODE) {
          const text = textNode.textContent || '';
          const offset = range.startOffset;
          
          // Find word boundaries - word characters include letters, numbers, and underscores
          let start = offset;
          let end = offset;
          
          // Move start backward to find word start (non-whitespace)
          while (start > 0 && /\S/.test(text[start - 1])) {
            start--;
          }
          
          // Move end forward to find word end (non-whitespace)
          while (end < text.length && /\S/.test(text[end])) {
            end++;
          }
          
          if (start < end) {
            const wordRange = document.createRange();
            wordRange.setStart(textNode, start);
            wordRange.setEnd(textNode, end);
            const wordText = wordRange.toString().trim();
            if (wordText) {
              return { text: wordText, range: wordRange };
            }
          }
        }
      }
      
      // Fallback: try to find text node at click position
      const elementAtPoint = document.elementFromPoint(clickX, clickY);
      if (!elementAtPoint) return { text: '', range: null };
      
      // Find the text node containing the click
      const walker = document.createTreeWalker(
        elementAtPoint,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let textNode: Node | null = null;
      while ((textNode = walker.nextNode())) {
        const testRange = document.createRange();
        testRange.selectNodeContents(textNode);
        const rect = testRange.getBoundingClientRect();
        
        if (rect.width > 0 && rect.height > 0 &&
            clickX >= rect.left && clickX <= rect.right && 
            clickY >= rect.top && clickY <= rect.bottom) {
          // Found the text node, estimate offset based on click position
          const text = textNode.textContent || '';
          if (!text.trim()) continue;
          
          // Estimate character offset based on click position within the text node
          const textOffset = Math.max(0, Math.min(
            Math.floor(((clickX - rect.left) / rect.width) * text.length),
            text.length - 1
          ));
          
          // Find word boundaries
          let start = textOffset;
          let end = textOffset;
          
          // Move start backward to find word start
          while (start > 0 && /\S/.test(text[start - 1])) {
            start--;
          }
          
          // Move end forward to find word end
          while (end < text.length && /\S/.test(text[end])) {
            end++;
          }
          
          if (start < end) {
            const wordRange = document.createRange();
            wordRange.setStart(textNode, start);
            wordRange.setEnd(textNode, end);
            const wordText = wordRange.toString().trim();
            if (wordText) {
              return { text: wordText, range: wordRange };
            }
          }
        }
      }
    } catch (error) {
      console.warn('Error finding word at position:', error);
    }
    
    return { text: '', range: null };
  };

  // Shared helper: open comment dialog from a resolved range + selected text.
  const openCommentDialogFromRange = (range: Range, selectedText: string, docxHost: HTMLDivElement) => {
    if (!selectedText.trim()) return;
    const docxRect = docxHost.getBoundingClientRect();
    const rangeRect = range.getBoundingClientRect();
    const x = rangeRect.left + rangeRect.width / 2 - docxRect.left;
    const y = rangeRect.top - docxRect.top;
    setSelectedPosition({ x, y, selectedText: selectedText.trim(), range });
    setCommentDialogOpen(true);
  };

  // Opens modal only when user finishes highlighting text (mouseup with a non-collapsed selection).
  const handleDocumentMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.comment-marker')) return;

    if (!canEditMockSurvey) return;

    const docxHost = docxHostRef.current;
    if (!docxHost) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    if (!selectedText) return;

    const ancestor = range.commonAncestorContainer;
    const node = ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentNode : ancestor;
    if (!docxHost.contains(node as Node)) return;

    openCommentDialogFromRange(range, selectedText, docxHost);
  };

  // Opens modal on double-click (single word selected, Word-like UX).
  const handleDocumentDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.comment-marker')) return;

    if (!canEditMockSurvey) {
      toast.error('You do not have permission to add comments');
      return;
    }

    const docxHost = docxHostRef.current;
    if (!docxHost) return;

    const wordAtPosition = findWordAtPosition(e.clientX, e.clientY, docxHost);
    if (wordAtPosition.text && wordAtPosition.range) {
      openCommentDialogFromRange(wordAtPosition.range, wordAtPosition.text, docxHost);
    }
  };

  const handleAddComment = async () => {
    if (!selectedPosition || !commentText.trim()) return;

    // Check if user has add permission for Mock Survey (by feature name)
    if (!canEditMockSurvey) {
      toast.error('You do not have permission to add comments');
      return;
    }

    // Capture surrounding context for better matching
    let contextText = '';
    let beforeText = '';
    let afterText = '';
    let paragraphIndex = -1;
    let occurrence = 1; // Default to 1st occurrence
    let startOffset = -1;
    let endOffset = -1;
    let paragraphElement: Element | null = null; // Store paragraph element for final validation
    
    if (selectedPosition.range && docxHostRef.current) {
      try {
        // Get text before and after the selection
        const range = selectedPosition.range;
        const container = range.commonAncestorContainer;
        const docxHost = docxHostRef.current;
        
        // Find the paragraph/element containing the selection
        let element = container.nodeType === Node.TEXT_NODE 
          ? (container as Text).parentElement 
          : container as Element;
        
        // Walk up the DOM to find the nearest element tagged with data-p-index.
        while (element && element !== docxHost) {
          const pIndex = (element as HTMLElement).dataset?.pIndex;
          if (pIndex !== undefined) {
            paragraphElement = element;
            paragraphIndex = parseInt(pIndex, 10);
            break;
          }
          element = element.parentElement;
        }

        // If walk-up didn't find a tagged element, find the nearest <p> ancestor.
        if (!paragraphElement || paragraphIndex === -1) {
          let el: HTMLElement | null =
            container.nodeType === Node.TEXT_NODE
              ? (container as Text).parentElement
              : (container as HTMLElement);
          while (el && el !== docxHost) {
            if (el.tagName?.toLowerCase() === 'p') {
              paragraphElement = el;
              break;
            }
            el = el.parentElement;
          }

          if (paragraphElement) {
            const pIdx = (paragraphElement as HTMLElement).dataset?.pIndex;
            if (pIdx !== undefined) {
              paragraphIndex = parseInt(pIdx, 10);
            } else {
              // Re-tag body paragraphs (excluding header/footer) and retry.
              const bodyP = Array.from(docxHost.querySelectorAll('p')).filter(
                p => !p.closest('header') && !p.closest('footer')
              );
              bodyP.forEach((p, i) => { (p as HTMLElement).dataset.pIndex = String(i); });
              const idx = bodyP.indexOf(paragraphElement as HTMLParagraphElement);
              if (idx !== -1) paragraphIndex = idx;
            }
          }
        }
        
        if (paragraphElement) {
          // Normalize paragraph text to match backend processing
          // Backend normalizes: replace non-breaking space (\u00A0) with regular space, \r\n with \n
          let fullParagraphText = paragraphElement.textContent || '';
          // Normalize according to backend: replace \u00A0 with space, \r\n with \n
          fullParagraphText = fullParagraphText.replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n');
          
          const selectedText = (selectedPosition.selectedText || '').replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n');

          // Calculate offsets using Range object (most accurate method)
          if (range && paragraphElement) {
            const startContainer = range.startContainer;
            const endContainer = range.endContainer;
            
            // Helper function to find cumulative character offset to a specific text node within paragraph
            const findTextNodeOffset = (targetTextNode: Node): number => {
              const walker = document.createTreeWalker(
                paragraphElement as Node,
                NodeFilter.SHOW_TEXT,
                null
              );
              let node;
              let cumulativeOffset = 0;
              while ((node = walker.nextNode())) {
                if (node === targetTextNode) {
                  return cumulativeOffset;
                }
                cumulativeOffset += (node.textContent || '').length;
              }
              return -1; // Not found
            };

            // Calculate start offset
            if (startContainer.nodeType === Node.TEXT_NODE) {
              const textNodeOffset = findTextNodeOffset(startContainer);
              if (textNodeOffset >= 0) {
                startOffset = textNodeOffset + range.startOffset;
              }
            } else {
              // If startContainer is an element, find the first text node within it
              const walker = document.createTreeWalker(
                startContainer,
                NodeFilter.SHOW_TEXT,
                null
              );
              const firstTextNode = walker.nextNode();
              if (firstTextNode) {
                const textNodeOffset = findTextNodeOffset(firstTextNode);
                if (textNodeOffset >= 0) {
                  startOffset = textNodeOffset + range.startOffset;
                }
              }
            }

            // Calculate end offset
            if (endContainer.nodeType === Node.TEXT_NODE) {
              const textNodeOffset = findTextNodeOffset(endContainer);
              if (textNodeOffset >= 0) {
                endOffset = textNodeOffset + range.endOffset;
              }
            } else {
              // If endContainer is an element, find the last text node within it
              const walker = document.createTreeWalker(
                endContainer,
                NodeFilter.SHOW_TEXT,
                null
              );
              let lastTextNode = null;
              let node;
              while ((node = walker.nextNode())) {
                lastTextNode = node;
              }
              if (lastTextNode) {
                const textNodeOffset = findTextNodeOffset(lastTextNode);
                if (textNodeOffset >= 0) {
                  endOffset = textNodeOffset + (lastTextNode.textContent || '').length;
                }
              }
            }

            // Validate and clamp offsets to paragraph bounds
            const paraLength = fullParagraphText.length;
            
            // Always validate offsets are within bounds
            const isValidOffset = (offset: number) => offset >= 0 && offset <= paraLength;
            
            // If calculated offsets are invalid, use fallback method
            if (!isValidOffset(startOffset) || !isValidOffset(endOffset) || startOffset > endOffset) {
              console.warn('⚠️ Calculated offsets invalid, using fallback:', {
                startOffset,
                endOffset,
                paraLength,
                selectedText,
                fullParagraphText: fullParagraphText.substring(0, 50) + '...'
              });
              
              // Fallback: use indexOf to find selectedText position
              const selectedIndex = fullParagraphText.indexOf(selectedText);
              if (selectedIndex !== -1) {
                startOffset = selectedIndex;
                endOffset = Math.min(selectedIndex + selectedText.length, paraLength);
              } else {
                // Last resort: try normalized search
                const normalizedPara = fullParagraphText.toLowerCase();
                const normalizedSelected = selectedText.toLowerCase();
                const normalizedIndex = normalizedPara.indexOf(normalizedSelected);
                if (normalizedIndex !== -1) {
                  startOffset = normalizedIndex;
                  endOffset = Math.min(normalizedIndex + selectedText.length, paraLength);
                } else {
                  // Final fallback: set to beginning of paragraph
                  startOffset = 0;
                  endOffset = Math.min(selectedText.length, paraLength);
                }
              }
            } else {
              // Ensure endOffset is valid even if startOffset is valid
              if (endOffset < startOffset) {
                endOffset = startOffset + Math.min(selectedText.length, paraLength - startOffset);
              }
              if (endOffset > paraLength) {
                endOffset = paraLength;
              }
              // Final validation - ensure startOffset doesn't exceed endOffset
              if (startOffset > endOffset) {
                startOffset = Math.max(0, endOffset - selectedText.length);
              }
            }
            
            // Final bounds check before using offsets
            startOffset = Math.max(0, Math.min(startOffset, paraLength));
            endOffset = Math.max(startOffset, Math.min(endOffset, paraLength));
            
            // Handle cursor-position comments (no text selected): ensure endOffset > startOffset
            // This handles collapsed ranges where startOffset == endOffset
            if (startOffset === endOffset && paraLength > 0) {
              if (endOffset >= paraLength) {
                // If at the end, set to last character position
                startOffset = Math.max(0, paraLength - 1);
                endOffset = paraLength;
              } else {
                // Otherwise, increment endOffset by 1
                endOffset = Math.min(startOffset + 1, paraLength);
              }
            }
            
            console.log('✅ Final offsets:', {
              startOffset,
              endOffset,
              paraLength,
              selectedText,
              paragraphTextPreview: fullParagraphText.substring(0, 50) + (fullParagraphText.length > 50 ? '...' : ''),
              isValid: startOffset >= 0 && endOffset <= paraLength && startOffset < endOffset
            });

            // Get text before (up to 100 chars) and after (up to 100 chars)
            const beforeStart = Math.max(0, startOffset - 100);
            const afterEnd = Math.min(paraLength, endOffset + 100);

            beforeText = fullParagraphText.substring(beforeStart, startOffset).trim();
            afterText = fullParagraphText.substring(endOffset, afterEnd).trim();
            contextText = beforeText + ' ' + selectedText + ' ' + afterText;
          } else {
            // Fallback: use indexOf if Range is not available
            const selectedIndex = fullParagraphText.indexOf(selectedText);
            if (selectedIndex !== -1) {
              startOffset = selectedIndex;
              endOffset = selectedIndex + selectedText.length;
              
              // Get text before (up to 100 chars) and after (up to 100 chars)
              const beforeStart = Math.max(0, selectedIndex - 100);
              const afterEnd = Math.min(fullParagraphText.length, selectedIndex + selectedText.length + 100);

              beforeText = fullParagraphText.substring(beforeStart, selectedIndex).trim();
              afterText = fullParagraphText.substring(selectedIndex + selectedText.length, afterEnd).trim();
              contextText = beforeText + ' ' + selectedText + ' ' + afterText;
            } else {
              contextText = fullParagraphText;
              // Set default offsets if we can't find the text
              // If no text selected (cursor position), set to beginning with endOffset > startOffset
              if (selectedText.length === 0 && fullParagraphText.length > 0) {
                startOffset = 0;
                endOffset = Math.min(1, fullParagraphText.length);
              } else {
                startOffset = 0;
                endOffset = Math.min(selectedText.length || 1, fullParagraphText.length);
              }
            }
          }

          // Find paragraph index if not already found from data-p-index.
          if (paragraphIndex === -1 && paragraphElement) {
            const bodyP = Array.from(docxHost.querySelectorAll('p')).filter(
              p => !p.closest('header') && !p.closest('footer')
            );
            const idx = bodyP.indexOf(paragraphElement as HTMLParagraphElement);
            if (idx !== -1) paragraphIndex = idx;
          }

          // Calculate occurrence number within the paragraph
          if (selectedPosition.selectedText && startOffset >= 0) {
            const paragraphText = paragraphElement.textContent || '';
            const searchText = selectedPosition.selectedText;
            const normalizedPara = paragraphText.toLowerCase();
            const normalizedSearch = searchText.toLowerCase();

            // Count occurrences up to the selection position
            let count = 0;
            let searchIndex = 0;

            while (true) {
              const foundIndex = normalizedPara.indexOf(normalizedSearch, searchIndex);
              if (foundIndex === -1) break;

              const selectionStartInPara = paragraphText.indexOf(searchText, foundIndex);
              if (selectionStartInPara !== -1 && selectionStartInPara <= startOffset) {
                occurrence = count + 1;
              }

              count++;
              searchIndex = foundIndex + 1;
            }
          }
        } else {
          // Fallback: get text from container
          if (container.nodeType === Node.TEXT_NODE) {
            const textNode = container as Text;
            const text = textNode.textContent || '';
            const offset = range.startOffset;
            
            const beforeStart = Math.max(0, offset - 100);
            const afterEnd = Math.min(text.length, offset + (range.endOffset - range.startOffset) + 100);
            contextText = text.substring(beforeStart, afterEnd);
            beforeText = text.substring(beforeStart, offset);
            afterText = text.substring(offset + (range.endOffset - range.startOffset), afterEnd);
          }
        }
      } catch (e) {
        console.warn('Failed to capture context:', e);
      }
    }

    const newComment: CommentData = {
      id: Date.now().toString(),
      x: selectedPosition.x,
      y: selectedPosition.y,
      text: commentText,
      author: getCurrentUser(),
      timestamp: new Date(),
      selectedText: selectedPosition.selectedText,
      range: selectedPosition.range || null,
      contextText: contextText || undefined,
      beforeText: beforeText || undefined,
      afterText: afterText || undefined,
      paragraphIndex: paragraphIndex >= 0 ? paragraphIndex : undefined,
      occurrence: occurrence,
    };

    // Log comment details to console
    console.log('📝 Comment Added:', {
      id: newComment.id,
      commentText: newComment.text,
      selectedText: newComment.selectedText,
      paragraphIndex: newComment.paragraphIndex,
      occurrence: newComment.occurrence,
      author: newComment.author,
      timestamp: newComment.timestamp.toISOString(),
      position: {
        x: newComment.x,
        y: newComment.y,
      },
      context: {
        beforeText: newComment.beforeText?.substring(0, 50) + (newComment.beforeText && newComment.beforeText.length > 50 ? '...' : ''),
        afterText: newComment.afterText?.substring(0, 50) + (newComment.afterText && newComment.afterText.length > 50 ? '...' : ''),
      },
      dataForBackend: {
        paragraphIndex: newComment.paragraphIndex,
        occurrence: newComment.occurrence,
        selectedText: newComment.selectedText,
        commentText: newComment.text,
      },
    });

    setComments([...comments, newComment]);
    setCommentText('');
    setCommentDialogOpen(false);
    setSelectedPosition(null);
    
    // Save comment to API if fileId and onSaveComment are provided.
    // paragraphIndex may be -1 or undefined if DOM lookup failed; warn user.
    const resolvedParagraphIndex: number =
      (paragraphIndex !== undefined && (paragraphIndex as number) >= 0)
        ? (paragraphIndex as number)
        : -1;
    if (resolvedParagraphIndex === -1) {
      console.warn('⚠️ Could not resolve paragraphIndex — API call skipped.');
      toast.warning('Could not determine paragraph position. Please try re-selecting the text.');
    }
    if (fileId && onSaveComment && resolvedParagraphIndex >= 0 && startOffset >= 0 && endOffset >= 0) {
      paragraphIndex = resolvedParagraphIndex;
      try {
        // Get paragraph text to validate offsets one more time
        // Normalize the same way as backend: replace \u00A0 with space, \r\n with \n
        let finalParaText = '';
        if (paragraphElement) {
          finalParaText = (paragraphElement.textContent || '').replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n');
        }
        
        const normalizedSelectedText = (selectedPosition.selectedText || '').replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n');
        
        let finalStartOffset = startOffset;
        let finalEndOffset = endOffset;
        const paraLength = finalParaText.length;
        
        // Clamp to paragraph bounds first.
        finalStartOffset = Math.max(0, Math.min(finalStartOffset, paraLength));
        finalEndOffset = Math.max(finalStartOffset, Math.min(finalEndOffset, paraLength));
        
        // Verify the Range-calculated offsets actually extract the selected text.
        const extractedSubstring = finalParaText.slice(finalStartOffset, finalEndOffset);
        let offsetsValid = extractedSubstring === normalizedSelectedText;
        
        // Only recalculate if the Range offsets are wrong.
        // Use the nth-occurrence (from the `occurrence` variable) so that
        // duplicate text within the same paragraph gets the correct position.
        if (!offsetsValid && normalizedSelectedText) {
          let searchPos = 0;
          let nthIndex = -1;
          for (let occ = 0; occ < occurrence; occ++) {
            nthIndex = finalParaText.indexOf(normalizedSelectedText, searchPos);
            if (nthIndex === -1) break;
            searchPos = nthIndex + 1;
          }
          if (nthIndex === -1) {
            // Fallback: case-insensitive search for the nth occurrence.
            const lowerPara = finalParaText.toLowerCase();
            const lowerSel = normalizedSelectedText.toLowerCase();
            searchPos = 0;
            for (let occ = 0; occ < occurrence; occ++) {
              nthIndex = lowerPara.indexOf(lowerSel, searchPos);
              if (nthIndex === -1) break;
              searchPos = nthIndex + 1;
            }
          }
          if (nthIndex !== -1) {
            console.warn('⚠️ Offsets mismatch, recalculating to occurrence', occurrence, ':', {
              originalStartOffset: finalStartOffset,
              originalEndOffset: finalEndOffset,
              nthIndex,
              extractedSubstring,
              normalizedSelectedText
            });
            finalStartOffset = nthIndex;
            finalEndOffset = Math.min(nthIndex + normalizedSelectedText.length, paraLength);
            offsetsValid = true;
          }
        }
        
        // Final bounds check after potential recalculation
        finalStartOffset = Math.max(0, Math.min(finalStartOffset, paraLength));
        finalEndOffset = Math.max(finalStartOffset, Math.min(finalEndOffset, paraLength));
        
        // Handle cursor-position comments (no text selected): ensure endOffset > startOffset
        // Backend requires startOffset < endOffset, so if they're equal, increment endOffset by 1
        if (finalStartOffset === finalEndOffset && paraLength > 0) {
          // If at the end of paragraph, set to last character position
          if (finalEndOffset >= paraLength) {
            finalStartOffset = Math.max(0, paraLength - 1);
            finalEndOffset = paraLength;
          } else {
            // Otherwise, increment endOffset by 1 to satisfy backend validation
            finalEndOffset = Math.min(finalStartOffset + 1, paraLength);
          }
        }
        
        const finalExtracted = finalParaText.slice(finalStartOffset, finalEndOffset);
        console.log('🔍 Sanity check:', {
          paragraphIndex,
          paraLength,
          finalStartOffset,
          finalEndOffset,
          extractedSubstring: finalExtracted,
          normalizedSelectedText,
          offsetsValid: finalExtracted === normalizedSelectedText || normalizedSelectedText === '',
          paragraphTextPreview: finalParaText.substring(0, 50) + (finalParaText.length > 50 ? '...' : '')
        });
        
        // Only proceed if offsets are valid
        // Allow cursor-position comments (empty selectedText) or matching offsets
        const isValidCursorComment = normalizedSelectedText === '' && finalStartOffset < finalEndOffset;
        const isValidSelectionComment = offsetsValid && finalStartOffset < finalEndOffset;
        
        if ((isValidCursorComment || isValidSelectionComment) && finalEndOffset <= paraLength) {
          const author = getCurrentUser();
          const initials = getUserInitials();
          
          // Always include paragraph_text and selected_text for server-side validation/fallback
          await onSaveComment({
            file_id: fileId,
            file_name: fileName || 'document.docx', // Include file name
            paragraphIndex: paragraphIndex,
            paragraphText: finalParaText, // Include full paragraph text for server validation
            startOffset: finalStartOffset,
            endOffset: finalEndOffset,
            selectedText: normalizedSelectedText,
            commentText: commentText,
            author: author,
            initials: initials,
          });
          console.log('✅ Comment saved to API successfully');
        } else {
          console.error('❌ Invalid offsets after final validation:', {
            finalStartOffset,
            finalEndOffset,
            paraLength,
            originalStartOffset: startOffset,
            originalEndOffset: endOffset
          });
        }
      } catch (error) {
        console.error('❌ Failed to save comment to API:', error);
        // Optionally show error message to user
      }
    }
    
    // Clear selection after adding comment
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
  };

  const handleCommentClick = (comment: CommentData) => {
    const container = containerRef.current;
    const docxHost = docxHostRef.current;
    if (!container || !docxHost) return;

    // Activate highlight first so the overlay re-renders with strong colour.
    setHighlightedCommentId(comment.id);
    setTimeout(() => setHighlightedCommentId(null), 2500);

    // --- Scroll document to the highlighted text overlay ---
    // Priority 1: use the rendered highlight overlay spans (most accurate).
    const overlays = docxHost.querySelectorAll<HTMLElement>(
      `[data-comment-highlight-overlay][data-comment-id="${comment.id}"]`
    );
    if (overlays.length > 0) {
      const firstOverlay = overlays[0];
      const containerRect = container.getBoundingClientRect();
      const overlayRect = firstOverlay.getBoundingClientRect();
      const scrollTop =
        container.scrollTop + overlayRect.top - containerRect.top - containerRect.height / 2 + overlayRect.height / 2;
      container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    } else if (comment.range) {
      // Priority 2: use the stored Range object.
      try {
        const rects = comment.range.getClientRects();
        if (rects.length > 0) {
          const rect = rects[0];
          const containerRect = container.getBoundingClientRect();
          const scrollTop =
            container.scrollTop + rect.top - containerRect.top - containerRect.height / 2 + rect.height / 2;
          container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
        }
      } catch (_) { /* range may be stale */ }
    } else if (comment.y > 0) {
      // Priority 3: fall back to stored y coordinate.
      const containerRect = container.getBoundingClientRect();
      container.scrollTo({
        top: Math.max(0, container.scrollTop + comment.y - containerRect.height / 2),
        behavior: 'smooth',
      });
    }

    // --- Scroll right panel to the matching comment card ---
    const panelItem = document.querySelector<HTMLElement>(
      `[data-comment-panel-id="${comment.id}"]`
    );
    if (panelItem) {
      panelItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleDeleteComment = async (commentId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the comment click navigation

    // Check if user has delete permission for Mock Survey
    if (!canEditMockSurvey) {
      toast.error('You do not have permission to delete comments');
      return;
    }

    // Set loading state immediately
    setDeletingCommentId(commentId);

    // Store the comment to delete for potential restoration
    const allComments = comments.flatMap(c => [c, ...(c.replies || [])]);
    const commentToDelete = allComments.find(c => c.id === commentId);

    if (!commentToDelete) {
      console.error('Comment to delete not found:', commentId);
      setDeletingCommentId(null);
      return;
    }

    // Keep comment visible until deletion succeeds - no optimistic update

    try {
      // If this is an API comment, call the remove API
      if (commentId.startsWith('api-comment-') && fileId) {
        try {
          // Extract the numeric comment ID from the comment ID string
          const apiCommentId = parseInt(commentId.replace('api-comment-', ''), 10);

          if (isNaN(apiCommentId)) {
            console.error('Invalid API comment ID format:', commentId);
            setDeletingCommentId(null);
            toast.error('Invalid comment ID format');
            return;
          }

          const removeRequest = {
            author: commentToDelete.author,
            commentId: apiCommentId,
            commentText: commentToDelete.text,
            endOffset: commentToDelete.endOffset || 0,
            file_id: parseInt(String(fileId), 10),
            paragraphIndex: commentToDelete.paragraphIndex || 0,
            startOffset: commentToDelete.startOffset || 0,
          };

          console.log('🗑️ Calling remove comment API:', removeRequest);
          const result = await mockSurveyService.removeComment(removeRequest);
          console.log('📥 Remove comment API response:', result);

          if (!result.success) {
            console.error('❌ Failed to remove comment from backend:', result.message);
            setDeletingCommentId(null);
            toast.error(result.message || 'Failed to remove comment from backend');
            return;
          }

          console.log('✅ Comment removed from backend successfully');
          
          // Reload comments from backend after successful deletion
          // This will remove the comment from the UI
          if (onCommentDeleted) {
            try {
              console.log('🔄 Reloading comments from backend...');
              await onCommentDeleted({
                commentId: apiCommentId,
                fileId,
                commentText: commentToDelete.text,
                paragraphIndex: commentToDelete.paragraphIndex,
              });
              console.log('✅ Comments reloaded from backend');
            } catch (reloadError) {
              console.error('❌ Failed to reload comments after deletion:', reloadError);
              // If reload fails, manually remove the comment from UI
              setComments(prevComments => {
                if (commentToDelete?.parentId) {
                  return prevComments.map(comment => {
                    if (comment.id === commentToDelete.parentId && comment.replies) {
                      return {
                        ...comment,
                        replies: comment.replies.filter(reply => reply.id !== commentId)
                      };
                    }
                    return comment;
                  });
                } else {
                  return prevComments.filter(comment => comment.id !== commentId);
                }
              });
              delete commentMarkerRefs.current[commentId];
            }
          } else {
            // If no reload callback, manually remove the comment from UI
            setComments(prevComments => {
              if (commentToDelete?.parentId) {
                return prevComments.map(comment => {
                  if (comment.id === commentToDelete.parentId && comment.replies) {
                    return {
                      ...comment,
                      replies: comment.replies.filter(reply => reply.id !== commentId)
                    };
                  }
                  return comment;
                });
              } else {
                return prevComments.filter(comment => comment.id !== commentId);
              }
            });
            delete commentMarkerRefs.current[commentId];
          }
          
          // Show success message after successful deletion
          toast.success('Comment removed successfully');
        } catch (apiError: any) {
          console.error('❌ API error removing comment:', apiError);
          setDeletingCommentId(null);
          toast.error(apiError.message || 'Failed to remove comment from backend');
          return;
        }
      } else {
        // For non-API comments (DOCX embedded comments), remove from UI after showing success
        setComments(prevComments => {
          if (commentToDelete?.parentId) {
            return prevComments.map(comment => {
              if (comment.id === commentToDelete.parentId && comment.replies) {
                return {
                  ...comment,
                  replies: comment.replies.filter(reply => reply.id !== commentId)
                };
              }
              return comment;
            });
          } else {
            return prevComments.filter(comment => comment.id !== commentId);
          }
        });
        delete commentMarkerRefs.current[commentId];
        toast.success('Comment removed successfully');
      }
    } catch (error: any) {
      console.error('❌ Error deleting comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      // Clear loading state
      setDeletingCommentId(null);
    }
  };

  const handleEditComment = (commentId: string, currentText: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if user has update permission for Mock Survey
    if (!canEditMockSurvey) {
      toast.error('You do not have permission to edit comments');
      return;
    }
    
    setEditingCommentId(commentId);
    setEditingCommentText(currentText);
  };

  const handleSaveEdit = async (commentId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!canEditMockSurvey) {
      toast.error('You do not have permission to edit comments');
      return;
    }

    if (!editingCommentText.trim()) {
      toast.error('Comment text cannot be empty');
      return;
    }

    const allComments = comments.flatMap(c => [c, ...(c.replies || [])]);
    const commentToEdit = allComments.find(c => c.id === commentId);

    if (!commentToEdit) {
      console.error('Comment to edit not found:', commentId);
      setEditingCommentId(null);
      setEditingCommentText('');
      return;
    }

    // Check if text actually changed
    if (editingCommentText.trim() === commentToEdit.text) {
      setEditingCommentId(null);
      setEditingCommentText('');
      return;
    }

    // Set saving state
    setSavingCommentId(commentId);

    try {
      // Optimistic update
      setComments(prevComments => {
        return prevComments.map(comment => {
          if (comment.id === commentId) {
            return { ...comment, text: editingCommentText.trim() };
          }
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map(reply =>
                reply.id === commentId ? { ...reply, text: editingCommentText.trim() } : reply
              )
            };
          }
          return comment;
        });
      });

      // If it's an API comment, try to update it on the backend
      if (commentId.startsWith('api-comment-') && onCommentUpdated) {
        console.log('🔄 Updating comment on backend with full payload...', {
          commentId,
          commentData: {
            text: editingCommentText.trim(),
            author: commentToEdit.author,
            paragraphIndex: commentToEdit.paragraphIndex,
            startOffset: commentToEdit.startOffset,
            endOffset: commentToEdit.endOffset,
            selectedText: commentToEdit.selectedText,
          }
        });
        
        await onCommentUpdated(commentId, { 
          text: editingCommentText.trim(),
          author: commentToEdit.author,
          initials: commentToEdit.initials,
          fileName: commentToEdit.fileName || fileName,
          paragraphIndex: commentToEdit.paragraphIndex,
          startOffset: commentToEdit.startOffset,
          endOffset: commentToEdit.endOffset,
          selectedText: commentToEdit.selectedText,
        });
        console.log('✅ Comment updated on backend successfully');
      }

      toast.success('Comment updated successfully');
    } catch (error: any) {
      console.error('❌ Error updating comment:', error);

      // Restore original comment on error
      setComments(prevComments => {
        return prevComments.map(comment => {
          if (comment.id === commentId) {
            return { ...comment, text: commentToEdit.text };
          }
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map(reply =>
                reply.id === commentId ? { ...reply, text: commentToEdit.text } : reply
              )
            };
          }
          return comment;
        });
      });

      toast.error('Failed to update comment');
    } finally {
      setSavingCommentId(null);
      setEditingCommentId(null);
      setEditingCommentText('');
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSavingCommentId(null);
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  // Calculate comment statistics
  const getCommentStats = useCallback(() => {
    const totalComments = comments.length;
    const totalReplies = comments.reduce((acc, comment) => acc + (comment.replies?.length || 0), 0);
    const authorsSet = new Set(comments.map(c => c.author));
    comments.forEach(comment => {
      comment.replies?.forEach(reply => authorsSet.add(reply.author));
    });
    const uniqueAuthors = authorsSet.size;
    const commentsWithSelections = comments.filter(c => c.selectedText).length;
    
    return {
      totalComments,
      totalReplies,
      totalAll: totalComments + totalReplies,
      uniqueAuthors,
      commentsWithSelections,
    };
  }, [comments]);

  const handleDownloadCommentsXLSX = () => {
    if (!canEditMockSurvey) {
      toast.error('You do not have permission to download comments');
      return;
    }

    if (comments.length === 0) {
      toast.info('No comments to export');
      return;
    }

    // Prepare data for Excel - flatten all comments including replies
    const commentsData: any[] = [];
    
    comments.forEach((comment, index) => {
      // Add main comment
      commentsData.push({
        'Comment #': index + 1,
        'Selected Text': comment.selectedText || '',
        'Comment Text': comment.text,
        'Author': comment.author,
        'Timestamp': comment.timestamp.toLocaleString(),
      });

      // Add replies if any
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach((reply, replyIndex) => {
          commentsData.push({
            'Comment #': `  Reply ${replyIndex + 1} to ${index + 1}`,
            'Selected Text': reply.selectedText || '',
            'Comment Text': reply.text,
            'Author': reply.author,
            'Timestamp': reply.timestamp.toLocaleString(),
          });
        });
      }
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(commentsData);

    // Set column widths for better readability
    const colWidths = [
      { wch: 15 }, // Comment #
      { wch: 30 }, // Selected Text
      { wch: 50 }, // Comment Text
      { wch: 20 }, // Author
      { wch: 25 }, // Timestamp
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Comments');

    // Generate filename
    const xlsxDate = new Date().toISOString().split('T')[0];
    let xlsxFileName: string;
    
    if (fileName) {
      // Remove file extension and add -comments-date.xlsx
      const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
      xlsxFileName = `${nameWithoutExt}-comments-${xlsxDate}.xlsx`;
    } else {
      xlsxFileName = `comments-${xlsxDate}.xlsx`;
    }

    // Download file
    XLSX.writeFile(wb, xlsxFileName);
    toast.success('Comments exported to Excel successfully');
  };

  // Helper function to find and embed comment at nth occurrence within a specific paragraph
  const findParagraphAndEmbedCommentNthOccurrence = (
    xmlDoc: Document,
    documentBody: Element,
    paragraphIndex: number,
    searchText: string,
    occurrence: number,
    numericCommentId: number
  ): boolean => {
    const paragraphs = documentBody.getElementsByTagName('w:p');
    if (paragraphIndex < 0 || paragraphIndex >= paragraphs.length) {
      console.warn(`Paragraph index ${paragraphIndex} out of range (total: ${paragraphs.length})`);
      return false;
    }

    const paragraph = paragraphs[paragraphIndex] as Element;
    const paragraphText = paragraph.textContent || '';

    // Find all occurrences of searchText in the paragraph (case-insensitive)
    const normalizedPara = paragraphText.toLowerCase();
    const normalizedSearch = searchText.toLowerCase().trim(); // Trim for search but preserve original
    let count = 0;
    let searchIndex = 0;
    const occurrencePositions: Array<{ start: number; end: number; text: string }> = [];

    while (true) {
      const foundIndex = normalizedPara.indexOf(normalizedSearch, searchIndex);
      if (foundIndex === -1) break;
      
      // Extract the actual text from the document at this position
      // Use the trimmed search length as a starting point, but we'll use the actual text
      const actualText = paragraphText.substring(foundIndex, foundIndex + normalizedSearch.length);
      
      // However, if the searchText was trimmed, we need to find the actual boundaries
      // The actual text in document might have spaces that were trimmed from searchText
      // So we extract using the search length, which should match the core text
      // The spaces are preserved because we're extracting from paragraphText (not normalized)
      
      count++;
      occurrencePositions.push({
        start: foundIndex,
        end: foundIndex + normalizedSearch.length,
        text: actualText // Preserve original case - spaces are already in paragraphText
      });
      searchIndex = foundIndex + 1;
    }

    if (occurrence < 1 || occurrence > count) {
      console.warn(`Occurrence ${occurrence} not found in paragraph ${paragraphIndex} (found ${count} occurrences of "${searchText}")`);
      return false;
    }

    const targetOccurrence = occurrencePositions[occurrence - 1];
    const targetPosition = targetOccurrence.start;
    const targetEndPosition = targetOccurrence.end;
    const actualSelectedText = targetOccurrence.text; // Use the exact text from document

    // Find the text node and position within the paragraph
    const runs = paragraph.getElementsByTagName('w:r');
    let cumulativePos = 0;

    for (let i = 0; i < runs.length; i++) {
      const run = runs[i] as Element;
      const textNodes = run.getElementsByTagName('w:t');

      for (let j = 0; j < textNodes.length; j++) {
        const textNode = textNodes[j];
        const text = textNode.textContent || '';
        const textLength = text.length;

        if (cumulativePos + textLength > targetPosition) {
          // Calculate how much of the selected text is in this text node
          const textNodeStartInPara = cumulativePos;
          
          // Use the exact end position from the document (preserves spacing)
          const selectionEndInPara = targetEndPosition;
          
          // Determine how much of the selection is in this current text node
          const selectionStartInNode = Math.max(0, targetPosition - textNodeStartInPara);
          const selectionEndInNode = Math.min(textLength, selectionEndInPara - textNodeStartInPara);
          
          // Extract the exact text from this node (preserves all spaces and original structure)
          const selectedTextInNode = text.substring(selectionStartInNode, selectionEndInNode);
          
          // Split the text node: before, selected, after
          // Use the EXACT text from the document node (preserves all spaces)
          const beforeText = text.substring(0, selectionStartInNode);
          const selectedText = selectedTextInNode; // Exact text from document node
          const afterText = text.substring(selectionEndInNode);
          
          // Log for debugging
          console.log(`🔍 Embedding comment: searchText="${searchText}", actualSelectedText="${actualSelectedText}", textNodeText="${text}", selectedTextInNode="${selectedTextInNode}"`);
          
          // Verify we're using the correct text
          if (selectedTextInNode.toLowerCase().trim() !== normalizedSearch.trim()) {
            console.warn(`⚠️ Text node content differs: expected "${searchText}", but node has "${selectedTextInNode}"`);
          }

          // Preserve original text node attributes (especially xml:space="preserve" for spacing)
          const originalAttributes: { name: string; value: string }[] = [];
          if (textNode.attributes) {
            for (let attrIdx = 0; attrIdx < textNode.attributes.length; attrIdx++) {
              const attr = textNode.attributes[attrIdx];
              originalAttributes.push({ name: attr.name, value: attr.value });
            }
          }

          // Create new text nodes with preserved attributes
          const beforeNode = xmlDoc.createElement('w:t');
          // Copy all attributes from original node
          originalAttributes.forEach(attr => {
            if (attr.name === 'xml:space' || attr.name.startsWith('xml:')) {
              beforeNode.setAttributeNS('http://www.w3.org/XML/1998/namespace', attr.name, attr.value);
            } else {
              beforeNode.setAttribute(attr.name, attr.value);
            }
          });
          beforeNode.textContent = beforeText;

          const selectedNode = xmlDoc.createElement('w:t');
          // Copy all attributes from original node
          originalAttributes.forEach(attr => {
            if (attr.name === 'xml:space' || attr.name.startsWith('xml:')) {
              selectedNode.setAttributeNS('http://www.w3.org/XML/1998/namespace', attr.name, attr.value);
            } else {
              selectedNode.setAttribute(attr.name, attr.value);
            }
          });
          selectedNode.textContent = selectedText; // Preserve exact text with spaces

          const afterNode = xmlDoc.createElement('w:t');
          // Copy all attributes from original node
          originalAttributes.forEach(attr => {
            if (attr.name === 'xml:space' || attr.name.startsWith('xml:')) {
              afterNode.setAttributeNS('http://www.w3.org/XML/1998/namespace', attr.name, attr.value);
            } else {
              afterNode.setAttribute(attr.name, attr.value);
            }
          });
          afterNode.textContent = afterText;

          // Create comment markers
          const commentRangeStart = xmlDoc.createElement('w:commentRangeStart');
          commentRangeStart.setAttribute('w:id', numericCommentId.toString());

          const commentRangeEnd = xmlDoc.createElement('w:commentRangeEnd');
          commentRangeEnd.setAttribute('w:id', numericCommentId.toString());

          // Replace the original text node with split nodes and comment markers
          const parentRun = textNode.parentNode as Element;

          // Insert before text
          if (beforeText) {
            parentRun.insertBefore(beforeNode, textNode);
          }

          // Insert comment range start
          parentRun.insertBefore(commentRangeStart, textNode);

          // Insert selected text
          parentRun.insertBefore(selectedNode, textNode);

          // Insert comment range end
          parentRun.insertBefore(commentRangeEnd, textNode);

          // Insert after text
          if (afterText) {
            parentRun.insertBefore(afterNode, textNode);
          }

          // Remove original text node
          parentRun.removeChild(textNode);

          // Add comment reference in the same run
          const commentReference = xmlDoc.createElement('w:commentReference');
          commentReference.setAttribute('w:id', numericCommentId.toString());
          parentRun.appendChild(commentReference);

          console.log(`✅ Comment embedded at paragraph ${paragraphIndex}, occurrence ${occurrence}`);
          return true;
        }

        cumulativePos += textLength;
      }
    }

    return false;
  };

  const handleDownload = async () => {
    if (!canEditMockSurvey) {
      toast.error('You do not have permission to download this document');
      return;
    }

    // Priority 1: Use backend download if available and fileId exists (backend embeds comments)
    // This ensures we always get the latest file from backend when fileId is available
    if (onDownloadFromBackend && fileId) {
      console.log('📥 Using backend download with fileId:', fileId);
      await onDownloadFromBackend();
      return;
    }
    
    // Fallback: Use frontend download (frontend embeds comments)
    // If no comments, just download the original file
    if (comments.length === 0) {
      if (onDownload) {
        onDownload(originalBlobRef.current);
      } else {
        const url = URL.createObjectURL(originalBlobRef.current);
        const a = document.createElement('a');
        a.href = url;
        a.download = `document-${Date.now()}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      return;
    }

    try {
      // Try to use JSZip to preserve original structure
      try {
        // Dynamically import JSZip
        const JSZip = (await import('jszip')).default;
        const originalBlobToUse = originalBlobRef.current;
        
        // Read the original DOCX file (which is a ZIP archive)
        const zip = await JSZip.loadAsync(await originalBlobToUse.arrayBuffer());
        
        // Read the main document.xml
        const documentXml = await zip.file('word/document.xml')?.async('string');
        if (!documentXml) {
          throw new Error('Could not find document.xml in DOCX file');
        }

        // Parse XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(documentXml, 'text/xml');
        const documentBody = xmlDoc.getElementsByTagName('w:body')[0];
        
        if (!documentBody) {
          throw new Error('Could not find document body');
        }

        // Remove all existing comment markers from document.xml to avoid duplicates
        const existingCommentRanges = xmlDoc.getElementsByTagName('w:commentRangeStart');
        const existingCommentEnds = xmlDoc.getElementsByTagName('w:commentRangeEnd');
        const existingCommentRefs = xmlDoc.getElementsByTagName('w:commentReference');
        
        // Remove comment range starts
        while (existingCommentRanges.length > 0) {
          const el = existingCommentRanges[0];
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }
        
        // Remove comment range ends
        while (existingCommentEnds.length > 0) {
          const el = existingCommentEnds[0];
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }
        
        // Remove comment references
        while (existingCommentRefs.length > 0) {
          const el = existingCommentRefs[0];
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }

        // Create a mapping from string comment IDs to numeric IDs (including replies)
        const commentIdMap = new Map<string, number>();
        let commentIndex = 0;
        const addCommentToMap = (comment: CommentData) => {
          commentIdMap.set(comment.id, commentIndex++);
          if (comment.replies && comment.replies.length > 0) {
            comment.replies.forEach(reply => addCommentToMap(reply));
          }
        };
        comments.forEach(comment => addCommentToMap(comment));

        // Helper function to find text in document with context-based matching
        const findTextInDocument = (
          searchText: string, 
          commentY?: number,
          contextText?: string,
          paragraphIndex?: number,
          beforeText?: string,
          afterText?: string
        ): { paragraph: Element; run: Element; textNode: Node; position: number } | null => {
          if (!searchText || !searchText.trim()) return null;
          
          const normalizedSearch = searchText.trim().toLowerCase();
          const paragraphs = documentBody.getElementsByTagName('w:p');
          
          // Collect all matches with their context
          const allMatches: Array<{ 
            paragraph: Element; 
            run: Element; 
            textNode: Node; 
            position: number; 
            paragraphIndex: number;
            contextScore: number;
          }> = [];
          
          for (let i = 0; i < paragraphs.length; i++) {
            const paragraph = paragraphs[i] as Element;
            const runs = paragraph.getElementsByTagName('w:r');
            
            // Get paragraph text for context matching
            const paragraphText = paragraph.textContent || '';
            const normalizedParagraphText = paragraphText.toLowerCase();
            
            for (let j = 0; j < runs.length; j++) {
              const run = runs[j] as Element;
              const textNodes = run.getElementsByTagName('w:t');
              
              for (let k = 0; k < textNodes.length; k++) {
                const textNode = textNodes[k];
                const text = textNode.textContent || '';
                
                // Check if the search text is in this text node
                const normalizedText = text.toLowerCase();
                let searchPos = normalizedText.indexOf(normalizedSearch);
                
                // Find all occurrences in this text node
                while (searchPos !== -1) {
                  // Calculate context score
                  let contextScore = 0;
                  
                  // Build a unique signature from text node: before + selected + after (20 chars each)
                  const textBeforeInNode = text.substring(Math.max(0, searchPos - 20), searchPos);
                  const textAfterInNode = text.substring(searchPos + normalizedSearch.length, Math.min(text.length, searchPos + normalizedSearch.length + 20));
                  const nodeSignature = (textBeforeInNode + normalizedSearch + textAfterInNode).toLowerCase();
                  
                  // Find all occurrences of search text in paragraph
                  let searchTextPositions: number[] = [];
                  let searchPosInPara = normalizedParagraphText.indexOf(normalizedSearch);
                  while (searchPosInPara !== -1) {
                    searchTextPositions.push(searchPosInPara);
                    searchPosInPara = normalizedParagraphText.indexOf(normalizedSearch, searchPosInPara + 1);
                  }
                  
                  // Find which occurrence matches our unique signature
                  let posInPara = -1;
                  for (const candidatePos of searchTextPositions) {
                    const candidateBefore = normalizedParagraphText.substring(Math.max(0, candidatePos - 20), candidatePos);
                    const candidateAfter = normalizedParagraphText.substring(
                      candidatePos + normalizedSearch.length, 
                      Math.min(normalizedParagraphText.length, candidatePos + normalizedSearch.length + 20)
                    );
                    const candidateSignature = (candidateBefore + normalizedSearch + candidateAfter).toLowerCase();
                    
                    if (candidateSignature === nodeSignature) {
                      posInPara = candidatePos;
                      break;
                    }
                  }
                  
                  // If exact signature match found, use it; otherwise use first occurrence as fallback
                  if (posInPara === -1) {
                    if (searchTextPositions.length > 0) {
                      posInPara = searchTextPositions[0];
                    } else {
                      searchPos = normalizedText.indexOf(normalizedSearch, searchPos + 1);
                      continue; // Skip this match if we can't find position
                    }
                  }
                  
                  // Check before and after text with EXACT position matching
                  if (beforeText && beforeText.trim() && posInPara >= 0) {
                    const normalizedBefore = beforeText.trim().toLowerCase();
                    // Get text before the match (up to 200 chars)
                    const beforeStart = Math.max(0, posInPara - 200);
                    const beforeEnd = posInPara;
                    const beforeContext = normalizedParagraphText.substring(beforeStart, beforeEnd);
                    
                    // Check if beforeText ends exactly at the match position (most precise)
                    if (beforeContext.endsWith(normalizedBefore)) {
                      contextScore += 80; // Perfect exact match
                    } else {
                      // Check how close the beforeText is to the match
                      const beforeIndex = beforeContext.lastIndexOf(normalizedBefore);
                      if (beforeIndex !== -1) {
                        const distanceFromMatch = beforeEnd - (beforeStart + beforeIndex + normalizedBefore.length);
                        if (distanceFromMatch === 0) {
                          contextScore += 80; // Exact match
                        } else if (distanceFromMatch <= 5) {
                          contextScore += 60; // Very close (within 5 chars)
                        } else if (distanceFromMatch <= 15) {
                          contextScore += 40; // Close (within 15 chars)
                        } else if (distanceFromMatch <= 30) {
                          contextScore += 20; // Moderate (within 30 chars)
                        } else {
                          contextScore += 5; // Distant match
                        }
                      } else {
                        // Check for partial match (last 50-100 chars of beforeText)
                        const last50 = normalizedBefore.substring(Math.max(0, normalizedBefore.length - 50));
                        const last100 = normalizedBefore.substring(Math.max(0, normalizedBefore.length - 100));
                        if (beforeContext.endsWith(last50)) {
                          contextScore += 30;
                        } else if (beforeContext.endsWith(last100)) {
                          contextScore += 15;
                        } else if (beforeContext.includes(last50)) {
                          contextScore += 10;
                        }
                      }
                    }
                  }
                  
                  if (afterText && afterText.trim() && posInPara >= 0) {
                    const normalizedAfter = afterText.trim().toLowerCase();
                    const afterStart = posInPara + normalizedSearch.length;
                    const afterEnd = Math.min(normalizedParagraphText.length, afterStart + 200);
                    const afterContext = normalizedParagraphText.substring(afterStart, afterEnd);
                    
                    // Check if afterText starts exactly after the match (most precise)
                    if (afterContext.startsWith(normalizedAfter)) {
                      contextScore += 80; // Perfect exact match
                    } else {
                      // Check how close the afterText is to the match
                      const afterIndex = afterContext.indexOf(normalizedAfter);
                      if (afterIndex !== -1) {
                        if (afterIndex === 0) {
                          contextScore += 80; // Exact match
                        } else if (afterIndex <= 5) {
                          contextScore += 60; // Very close (within 5 chars)
                        } else if (afterIndex <= 15) {
                          contextScore += 40; // Close (within 15 chars)
                        } else if (afterIndex <= 30) {
                          contextScore += 20; // Moderate (within 30 chars)
                        } else {
                          contextScore += 5; // Distant match
                        }
                      } else {
                        // Check for partial match (first 50-100 chars of afterText)
                        const first50 = normalizedAfter.substring(0, Math.min(50, normalizedAfter.length));
                        const first100 = normalizedAfter.substring(0, Math.min(100, normalizedAfter.length));
                        if (afterContext.startsWith(first50)) {
                          contextScore += 30;
                        } else if (afterContext.startsWith(first100)) {
                          contextScore += 15;
                        } else if (afterContext.includes(first50)) {
                          contextScore += 10;
                        }
                      }
                    }
                  }
                  
                  // Bonus: if both before and after match exactly, add huge bonus
                  if (beforeText && afterText) {
                    const normalizedBefore = beforeText.trim().toLowerCase();
                    const normalizedAfter = afterText.trim().toLowerCase();
                    const beforeStart = Math.max(0, posInPara - 200);
                    const beforeEnd = posInPara;
                    const beforeContext = normalizedParagraphText.substring(beforeStart, beforeEnd);
                    const afterStart = posInPara + normalizedSearch.length;
                    const afterEnd = Math.min(normalizedParagraphText.length, afterStart + 200);
                    const afterContext = normalizedParagraphText.substring(afterStart, afterEnd);
                    
                    if (beforeContext.endsWith(normalizedBefore) && afterContext.startsWith(normalizedAfter)) {
                      contextScore += 100; // Perfect match on both sides
                    }
                  }
                  
                  // If we have full context text, check if it appears near this match
                  if (contextText && contextText.trim()) {
                    const normalizedContext = contextText.trim().toLowerCase();
                    const startPos = Math.max(0, posInPara - 100);
                    const endPos = Math.min(normalizedParagraphText.length, posInPara + normalizedSearch.length + 100);
                    const nearbyText = normalizedParagraphText.substring(startPos, endPos);
                    if (nearbyText.includes(normalizedContext)) {
                      contextScore += 15;
                    }
                  }
                  
                  // If we have paragraph index, prefer matches in that paragraph
                  if (paragraphIndex !== undefined && paragraphIndex >= 0) {
                    const distance = Math.abs(i - paragraphIndex);
                    if (distance === 0) {
                      contextScore += 30; // Exact paragraph match
                    } else if (distance <= 2) {
                      contextScore += 15 - (distance * 5); // Nearby paragraph
                    }
                  }
                  
                  // If we have y position, use it to estimate paragraph
                  if (commentY !== undefined && commentY > 0) {
                    const estimatedParagraphIndex = Math.min(
                      Math.max(0, Math.floor((commentY / 10000) * paragraphs.length)),
                      paragraphs.length - 1
                    );
                    const distance = Math.abs(i - estimatedParagraphIndex);
                    if (distance === 0) {
                      contextScore += 20;
                    } else if (distance <= 3) {
                      contextScore += 10 - (distance * 2);
                    }
                  }
                  
                  allMatches.push({ 
                    paragraph, 
                    run, 
                    textNode, 
                    position: searchPos,
                    paragraphIndex: i,
                    contextScore
                  });
                  searchPos = normalizedText.indexOf(normalizedSearch, searchPos + 1);
                }
              }
            }
          }
          
          if (allMatches.length === 0) {
            return null;
          }
          
          // If only one match, return it
          if (allMatches.length === 1) {
            return allMatches[0];
          }
          
          // Sort by context score (highest first) and return the best match
          allMatches.sort((a, b) => b.contextScore - a.contextScore);
          return allMatches[0];
        };

        // Add comments to the document
        const paragraphs = documentBody.getElementsByTagName('w:p');
        
        // For each comment, add comment markers to the document
        comments.forEach((comment, idx) => {
          const numericCommentId = commentIdMap.get(comment.id) ?? idx;
          
          let targetParagraph: Element | null = null;
          
          // Try paragraph-level anchoring first (most precise)
          if (comment.selectedText && comment.selectedText.trim() &&
              comment.paragraphIndex !== undefined && comment.occurrence !== undefined) {
            // Use the precise paragraph and occurrence approach
            const success = findParagraphAndEmbedCommentNthOccurrence(
              xmlDoc,
              documentBody,
              comment.paragraphIndex,
              comment.selectedText.trim(),
              comment.occurrence,
              numericCommentId
            );
            if (success) {
              // Comment marker added successfully using paragraph-level approach
              console.log(`✅ Comment ${numericCommentId + 1} embedded using paragraph-level anchoring: paragraphIndex=${comment.paragraphIndex}, occurrence=${comment.occurrence}, selectedText="${comment.selectedText}"`);
              return; // Skip to next comment
            } else {
              console.warn(`⚠️ Failed to embed comment ${numericCommentId + 1} using paragraph-level anchoring, falling back to context-based matching`);
            }
          }

          // Fallback: Try to find exact location using selectedText with context
          if (comment.selectedText && comment.selectedText.trim()) {
            const found = findTextInDocument(
              comment.selectedText,
              comment.y,
              comment.contextText,
              comment.paragraphIndex,
              comment.beforeText,
              comment.afterText
            );
            if (found) {
              targetParagraph = found.paragraph;
              
              // Split the text node at the found position
              const textNode = found.textNode;
              const text = textNode.textContent || '';
              const searchText = comment.selectedText.trim();
              const position = text.toLowerCase().indexOf(searchText.toLowerCase());
              
              if (position !== -1 && textNode.parentNode) {
                // Split text: before, selected, after
                const beforeText = text.substring(0, position);
                const selectedText = text.substring(position, position + searchText.length);
                const afterText = text.substring(position + searchText.length);
                
                // Create new text nodes
                const beforeNode = xmlDoc.createElement('w:t');
                beforeNode.textContent = beforeText;
                
                const selectedNode = xmlDoc.createElement('w:t');
                selectedNode.textContent = selectedText;
                
                const afterNode = xmlDoc.createElement('w:t');
                afterNode.textContent = afterText;
                
                // Insert comment markers around selected text
                const commentRangeStart = xmlDoc.createElement('w:commentRangeStart');
                commentRangeStart.setAttribute('w:id', numericCommentId.toString());
                
                const commentRangeEnd = xmlDoc.createElement('w:commentRangeEnd');
                commentRangeEnd.setAttribute('w:id', numericCommentId.toString());
                
                // Replace the original text node with the split nodes and comment markers
                const parentRun = textNode.parentNode as Element;
                
                // Insert before text
                if (beforeText) {
                  parentRun.insertBefore(beforeNode, textNode);
                }
                
                // Insert comment range start
                parentRun.insertBefore(commentRangeStart, textNode);
                
                // Insert selected text
                parentRun.insertBefore(selectedNode, textNode);
                
                // Insert comment range end
                parentRun.insertBefore(commentRangeEnd, textNode);
                
                // Insert after text
                if (afterText) {
                  parentRun.insertBefore(afterNode, textNode);
                }
                
                // Remove original text node
                parentRun.removeChild(textNode);
                
                // Add comment reference
                const commentReference = xmlDoc.createElement('w:commentReference');
                commentReference.setAttribute('w:id', numericCommentId.toString());
                parentRun.appendChild(commentReference);
                
                return; // Skip to next comment
              }
            }
          }
          
          // Fallback: if we couldn't find exact location, use approximate positioning
          if (!targetParagraph && paragraphs.length > 0) {
            // Use comment position to find approximate paragraph
            const paragraphIndex = Math.min(
              Math.max(0, Math.floor((comment.y / 10000) * paragraphs.length)),
              paragraphs.length - 1
            );
            targetParagraph = paragraphs[paragraphIndex] as Element;
          }
          
          if (!targetParagraph && paragraphs.length > 0) {
            targetParagraph = paragraphs[0] as Element;
          }

          if (targetParagraph) {
            // Add comment range start
            const commentRangeStart = xmlDoc.createElement('w:commentRangeStart');
            commentRangeStart.setAttribute('w:id', numericCommentId.toString());
            if (targetParagraph.firstChild) {
              targetParagraph.insertBefore(commentRangeStart, targetParagraph.firstChild);
            } else {
              targetParagraph.appendChild(commentRangeStart);
            }

            // Add comment range end
            const commentRangeEnd = xmlDoc.createElement('w:commentRangeEnd');
            commentRangeEnd.setAttribute('w:id', numericCommentId.toString());
            targetParagraph.appendChild(commentRangeEnd);

            // Get or create run element for comment reference
            let run = targetParagraph.getElementsByTagName('w:r')[0] as Element;
            if (!run) {
              run = xmlDoc.createElement('w:r');
              targetParagraph.appendChild(run);
            }

            // Add comment reference
            const commentReference = xmlDoc.createElement('w:commentReference');
            commentReference.setAttribute('w:id', numericCommentId.toString());
            run.appendChild(commentReference);
          }
        });

        // Create or update comments.xml
        let commentsXml = '';
        const existingCommentsFile = zip.file('word/comments.xml');
        
        if (existingCommentsFile) {
          commentsXml = await existingCommentsFile.async('string');
        }
        
        // Parse or create comments XML
        const commentsParser = new DOMParser();
        let commentsDoc: Document;
        let commentsRoot: Element;
        
        if (commentsXml) {
          commentsDoc = commentsParser.parseFromString(commentsXml, 'text/xml');
          commentsRoot = commentsDoc.getElementsByTagName('w:comments')[0];
        } else {
          commentsDoc = commentsParser.parseFromString(
            '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:comments xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"></w:comments>',
            'text/xml'
          );
          commentsRoot = commentsDoc.documentElement;
        }

        // Add each comment
        comments.forEach((comment, idx) => {
          const numericCommentId = commentIdMap.get(comment.id) ?? idx;
          
          const commentElement = commentsDoc.createElement('w:comment');
          commentElement.setAttribute('w:id', numericCommentId.toString());
          commentElement.setAttribute('w:author', comment.author || 'User');
          commentElement.setAttribute('w:date', comment.timestamp.toISOString());

          // Add comment text
          const commentPara = commentsDoc.createElement('w:p');
          const commentRun = commentsDoc.createElement('w:r');
          const commentTextNode = commentsDoc.createElement('w:t');
          commentTextNode.setAttribute('xml:space', 'preserve');
          commentTextNode.textContent = comment.text;
          commentRun.appendChild(commentTextNode);
          commentPara.appendChild(commentRun);
          commentElement.appendChild(commentPara);

          // Add selected text if available (important for positioning markers)
          if (comment.selectedText && comment.selectedText.trim()) {
            const selectedTextPara = commentsDoc.createElement('w:p');
            const selectedTextRun = commentsDoc.createElement('w:r');
            const selectedTextNode = commentsDoc.createElement('w:t');
            selectedTextNode.setAttribute('xml:space', 'preserve');
            selectedTextNode.textContent = `Selected: ${comment.selectedText}`;
            selectedTextRun.appendChild(selectedTextNode);
            selectedTextPara.appendChild(selectedTextRun);
            commentElement.appendChild(selectedTextPara);
          }

          commentsRoot.appendChild(commentElement);
        });

        // Update the comments.xml file in the ZIP
        const serializer = new XMLSerializer();
        zip.file('word/comments.xml', serializer.serializeToString(commentsDoc));
        
        // Update document.xml
        zip.file('word/document.xml', serializer.serializeToString(xmlDoc));

        // Update [Content_Types].xml to include comments if not present
        const contentTypesXml = await zip.file('[Content_Types].xml')?.async('string');
        if (contentTypesXml && !contentTypesXml.includes('comments.xml')) {
          const contentTypesDoc = parser.parseFromString(contentTypesXml, 'text/xml');
          const override = contentTypesDoc.createElement('Override');
          override.setAttribute('PartName', '/word/comments.xml');
          override.setAttribute('ContentType', 'application/vnd.openxmlformats-officedocument.wordprocessingml.comments+xml');
          contentTypesDoc.documentElement.appendChild(override);
          zip.file('[Content_Types].xml', serializer.serializeToString(contentTypesDoc));
        }

        // Update relationships if needed
        const relsXml = await zip.file('word/_rels/document.xml.rels')?.async('string');
        if (relsXml && !relsXml.includes('comments.xml')) {
          const relsDoc = parser.parseFromString(relsXml, 'text/xml');
          const relationship = relsDoc.createElement('Relationship');
          relationship.setAttribute('Id', `rId${comments.length + 100}`);
          relationship.setAttribute('Type', 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments');
          relationship.setAttribute('Target', 'comments.xml');
          relsDoc.documentElement.appendChild(relationship);
          zip.file('word/_rels/document.xml.rels', serializer.serializeToString(relsDoc));
        }

        // Generate the new DOCX file
        const newBlob = await zip.generateAsync({ 
          type: 'blob', 
          mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
        });
        
        if (onDownload) {
          onDownload(newBlob);
        } else {
          const url = URL.createObjectURL(newBlob);
          const a = document.createElement('a');
          a.href = url;
          // Use fileName if available, otherwise generate a name
          const downloadFileName = fileName 
            ? fileName.replace(/\.[^/.]+$/, '') + '-with-comments.docx'
            : `document-with-comments-${Date.now()}.docx`;
          a.download = downloadFileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        return;
      } catch (zipError) {
        console.warn('JSZip not available or failed, downloading original file:', zipError);
        // Fall through to download original
      }

      // Fallback: Download original file (preserves structure but without embedded comments)
      if (onDownload) {
        onDownload(originalBlobRef.current);
      } else {
        const url = URL.createObjectURL(originalBlobRef.current);
        const a = document.createElement('a');
        a.href = url;
        const downloadFileName = fileName || `document-${Date.now()}.docx`;
        a.download = downloadFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
      // Final fallback: download original file
      const url = URL.createObjectURL(originalBlobRef.current);
      const a = document.createElement('a');
      a.href = url;
      const downloadFileName = fileName || `document-${Date.now()}.docx`;
      a.download = downloadFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };


  const stats = getCommentStats();

  return (
    <ViewerContainer>
      <Toolbar>
        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
            onClick={handleDownload}
            disabled={downloading || !canEditMockSurvey}
            title={
              canEditMockSurvey
                ? 'Download document'
                : 'View-only access — download not permitted'
            }
          >
            {downloading ? 'Downloading...' : 'Download'}
          </Button>
        </Box>
        
        <Box display="flex" gap={1} alignItems="center">
          <Chip 
            icon={<Comment />} 
            label={`${stats.totalAll} Comments`} 
            color="primary" 
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
          <IconButton 
            size="small"
            onClick={() => setExpandOverview(!expandOverview)}
            title={expandOverview ? "Collapse overview" : "Expand overview"}
          >
            {expandOverview ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Toolbar>

      {/* Embedded Comment Overview Panel */}
      {showCommentOverview && (
        <Collapse in={expandOverview}>
          <CommentOverviewPanel elevation={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Assessment sx={{ color: '#5BBFD6', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#065B70' }}>
                  Comment Survey Overview
                </Typography>
              </Box>
              <Box display="flex" gap={2}>
                <Box textAlign="center">
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                    {stats.totalComments}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Comments
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                    {stats.totalReplies}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Replies
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                    {stats.uniqueAuthors}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Authors
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                    {stats.commentsWithSelections}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    With Highlights
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {comments.length > 0 && (
              <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  All Comments:
                </Typography>
                {comments.map((comment, index) => (
                  <CommentSummaryCard 
                    key={comment.id}
                    onClick={() => handleCommentClick(comment)}
                    elevation={2}
                  >
                    <CardContent sx={{ padding: '8px 12px !important' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start">
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Chip 
                              label={`#${comments.length - index}`} 
                              size="small" 
                              sx={{ 
                                backgroundColor: '#ff9800', 
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '11px',
                                height: '20px'
                              }} 
                            />
                            <Typography variant="caption" color="text.secondary">
                              by {comment.author}
                            </Typography>
                            {comment.replies && comment.replies.length > 0 && (
                              <Chip 
                                label={`${comment.replies.length} replies`} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: '#2196f3', 
                                  color: 'white',
                                  fontSize: '10px',
                                  height: '18px'
                                }} 
                              />
                            )}
                          </Box>
                          {comment.selectedText && (
                            <Box 
                              sx={{ 
                                backgroundColor: '#fff3cd', 
                                border: '1px solid #ffc107',
                                borderRadius: '4px',
                                padding: '4px 6px',
                                mb: 0.5,
                                fontSize: '11px',
                                fontStyle: 'italic',
                                color: '#856404'
                              }}
                            >
                              "{comment.selectedText.substring(0, 50)}{comment.selectedText.length > 50 ? '...' : ''}"
                            </Box>
                          )}
                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {comment.text.substring(0, 80)}{comment.text.length > 80 ? '...' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CommentSummaryCard>
                ))}
              </Box>
            )}
          </CommentOverviewPanel>
        </Collapse>
      )}

      <Box display="flex" flex={1} overflow="hidden">
        <DocumentArea
          ref={containerRef}
          onMouseUp={handleDocumentMouseUp}
          onDoubleClick={handleDocumentDoubleClick}
          style={{ flex: 1 }}
        >
          <DocxContainer>
            <div ref={docxHostRef} style={{ position: 'relative' }}>
              {/* Numbered pointer markers removed — text highlights serve as location indicators */}
            </div>
          </DocxContainer>
        </DocumentArea>

        {showCommentPanel && (
          <Box
            sx={{
              width: '300px',
              borderLeft: '1px solid #e0e0e0',
              padding: 2,
              overflowY: 'auto',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Comments</Typography>
              <Box display="flex" gap={1} alignItems="center">
                {comments.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadCommentsXLSX}
                    disabled={!canEditMockSurvey}
                    title={
                      canEditMockSurvey
                        ? 'Download comments as Excel'
                        : 'View-only access — download not permitted'
                    }
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderColor: '#5BBFD6',
                      color: '#065B70',
                      '&:hover': {
                        backgroundColor: '#EAF8FB',
                        borderColor: '#5BBFD6',
                      },
                    }}
                  >
                    Download XLSX
                  </Button>
                )}
              </Box>
            </Box>
            {comments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {canEditMockSurvey
                  ? 'No comments yet. Click on the document to add a comment.'
                  : 'No comments yet.'}
              </Typography>
            ) : (
              comments.map((comment) => (
                <React.Fragment key={comment.id}>
                  <CommentItem 
                    onClick={() => handleCommentClick(comment)}
                    data-comment-panel-id={comment.id}
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      outline: highlightedCommentId === comment.id ? '2px solid #ff9800' : 'none',
                      backgroundColor: highlightedCommentId === comment.id ? '#fff8e1' : undefined,
                      '&:hover': {
                        background: '#f0f0f0',
                        borderLeftColor: '#1976d2',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {editingCommentId === comment.id ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => handleSaveEdit(comment.id, e)}
                          disabled={savingCommentId === comment.id}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            padding: '4px',
                            color: '#4caf50',
                            '&:hover': {
                              backgroundColor: '#e8f5e8',
                              color: '#2e7d32',
                            },
                            '&:disabled': {
                              color: '#5BBFD6',
                            },
                          }}
                          title={savingCommentId === comment.id ? "Saving..." : "Save comment"}
                        >
                          {savingCommentId === comment.id ? (
                            <CircularProgress size={16} sx={{ color: '#5BBFD6' }} />
                          ) : (
                            <Save fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 40,
                            padding: '4px',
                            color: '#757575',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                              color: '#424242',
                            },
                          }}
                          title="Cancel edit"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {canEditMockSurvey && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleEditComment(comment.id, comment.text, e)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 40,
                              padding: '4px',
                              color: '#2196f3',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                color: '#0d47a1',
                              },
                            }}
                            title="Edit comment"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        {canEditMockSurvey && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteComment(comment.id, e)}
                            disabled={deletingCommentId === comment.id}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              padding: '4px',
                              color: '#d32f2f',
                              '&:hover': {
                                backgroundColor: '#ffebee',
                                color: '#c62828',
                              },
                              '&:disabled': {
                                color: '#5BBFD6',
                              },
                            }}
                            title={deletingCommentId === comment.id ? "Deleting..." : "Delete comment"}
                          >
                            {deletingCommentId === comment.id ? (
                              <CircularProgress size={16} sx={{ color: '#5BBFD6' }} />
                            ) : (
                              <Delete fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </>
                    )}
                    {comment.selectedText && (
                      <Box
                        sx={{
                          padding: '6px 8px',
                          marginBottom: '8px',
                          marginRight: '32px', // Space for delete button
                          background: '#fff3cd',
                          border: '1px solid #ffc107',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontStyle: 'italic',
                          color: '#856404',
                        }}
                      >
                        "{comment.selectedText}"
                      </Box>
                    )}
                    {editingCommentId === comment.id ? (
                      <Box sx={{ paddingRight: '32px', marginBottom: '8px' }}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={6}
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveEdit(comment.id, e as any);
                            } else if (e.key === 'Escape') {
                              handleCancelEdit(e as any);
                            }
                          }}
                          placeholder="Edit comment..."
                          size="small"
                          autoFocus
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '14px',
                            }
                          }}
                        />
                      </Box>
                    ) : (
                      <CommentText sx={{ paddingRight: '32px' }}>
                        Comment: {comment.text}
                      </CommentText>
                    )}
                    <CommentAuthor sx={{ paddingRight: '32px' }}>
                      {comment.author} • {comment.timestamp.toLocaleString()}
                    </CommentAuthor>
                  </CommentItem>
                  {/* Render replies nested under parent comment */}
                  {comment.replies && comment.replies.length > 0 && (
                    <Box sx={{ marginLeft: '24px', marginTop: '8px', borderLeft: '2px solid #e0e0e0', paddingLeft: '12px' }}>
                      {comment.replies.map((reply) => (
                        <CommentItem 
                          key={reply.id}
                          onClick={() => handleCommentClick(reply)}
                          sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            marginBottom: '8px',
                            backgroundColor: '#fafafa',
                            borderLeftColor: '#90caf9',
                            '&:hover': {
                              background: '#f0f0f0',
                              borderLeftColor: '#64b5f6',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {editingCommentId === reply.id ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => handleSaveEdit(reply.id, e)}
                                disabled={savingCommentId === reply.id}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  padding: '4px',
                                  color: '#4caf50',
                                  '&:hover': {
                                    backgroundColor: '#e8f5e8',
                                    color: '#2e7d32',
                                  },
                                  '&:disabled': {
                                    color: '#5BBFD6',
                                  },
                                }}
                                title={savingCommentId === reply.id ? "Saving..." : "Save reply"}
                              >
                                {savingCommentId === reply.id ? (
                                  <CircularProgress size={16} sx={{ color: '#5BBFD6' }} />
                                ) : (
                                  <Save fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 40,
                                  padding: '4px',
                                  color: '#757575',
                                  '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    color: '#424242',
                                  },
                                }}
                                title="Cancel edit"
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              {canEditMockSurvey && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleEditComment(reply.id, reply.text, e)}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 40,
                                    padding: '4px',
                                    color: '#2196f3',
                                    '&:hover': {
                                      backgroundColor: '#e3f2fd',
                                      color: '#0d47a1',
                                    },
                                  }}
                                  title="Edit reply"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              )}
                              {canEditMockSurvey && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteComment(reply.id, e)}
                                  disabled={deletingCommentId === reply.id}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    padding: '4px',
                                    color: '#d32f2f',
                                    '&:hover': {
                                      backgroundColor: '#ffebee',
                                      color: '#c62828',
                                    },
                                    '&:disabled': {
                                      color: '#5BBFD6',
                                    },
                                  }}
                                  title={deletingCommentId === reply.id ? "Deleting..." : "Delete reply"}
                                >
                                  {deletingCommentId === reply.id ? (
                                    <CircularProgress size={16} sx={{ color: '#5BBFD6' }} />
                                  ) : (
                                    <Delete fontSize="small" />
                                  )}
                                </IconButton>
                              )}
                            </>
                          )}
                          {reply.selectedText && (
                            <Box
                              sx={{
                                padding: '6px 8px',
                                marginBottom: '8px',
                                marginRight: '32px',
                                background: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                color: '#856404',
                              }}
                            >
                              "{reply.selectedText}"
                            </Box>
                          )}
                          {editingCommentId === reply.id ? (
                            <Box sx={{ paddingRight: '32px', marginBottom: '8px' }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={6}
                                value={editingCommentText}
                                onChange={(e) => setEditingCommentText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveEdit(reply.id, e as any);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit(e as any);
                                  }
                                }}
                                placeholder="Edit reply..."
                                size="small"
                                autoFocus
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '13px',
                                  }
                                }}
                              />
                            </Box>
                          ) : (
                            <CommentText sx={{ paddingRight: '32px', fontSize: '13px' }}>
                              Reply: {reply.text}
                            </CommentText>
                          )}
                          <CommentAuthor sx={{ paddingRight: '32px', fontSize: '11px' }}>
                            {reply.author} • {reply.timestamp.toLocaleString()}
                          </CommentAuthor>
                        </CommentItem>
                      ))}
                    </Box>
                  )}
                </React.Fragment>
              ))
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={commentDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setCommentDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '680px', md: '760px' },
            maxWidth: '760px',
            borderRadius: 2,
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.4)" },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Rethink Sans, sans-serif', fontWeight: 600, pb: 1 }}>
          Add Comment
          {selectedPosition?.selectedText && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                fontWeight: 400,
                fontSize: '13px',
                color: '#6b7280',
                fontStyle: 'italic',
                fontFamily: 'Rethink Sans, sans-serif',
              }}
            >
              On: &ldquo;{selectedPosition.selectedText.length > 120
                ? selectedPosition.selectedText.substring(0, 120) + '…'
                : selectedPosition.selectedText}&rdquo;
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            rows={6}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            variant="outlined"
            sx={{ fontFamily: 'Rethink Sans, sans-serif' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCommentDialogOpen(false)}
            sx={{ textTransform: 'none', fontFamily: 'Rethink Sans, sans-serif' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddComment}
            variant="contained"
            startIcon={<Send />}
            disabled={!commentText.trim() || !canEditMockSurvey}
            sx={{
              textTransform: 'none',
              fontFamily: 'Rethink Sans, sans-serif',
              backgroundColor: '#065B70',
              '&:hover': { backgroundColor: '#044a5a' },
            }}
          >
            Add Comment
          </Button>
        </DialogActions>
      </Dialog>
    </ViewerContainer>
  );
};

